import paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import type { Scene, SceneSelection } from "../canvas/Scene";
import { newId } from "../model/ids";
import { duplicateOffsetSubtreeForSlice } from "../model/projectOps";
import {
  descendantConstructionLineIds,
  isVisible,
  projectConstructionGroups,
  projectConstructionLines,
  projectTracingImages,
  tracingImageCenter,
  tracingImageRotation,
  type ConstructionLine,
  type TracingImage,
  type ConstraintKind,
  type ConstraintRef,
  type Project,
} from "../model/Project";
import {
  allowedRefKindsForConstraint,
  pruneConstraintsForDeletedLines,
  refsEqual,
  requiredRefsForConstraint,
} from "../model/constraints";
import {
  propagateCoincidentAfterAnchorDrag,
  pruneInvalidCenterConstraints,
  type SnapRefLite,
} from "../model/constraintEnforcement";
import {
  isOffsetDerived,
  offsetSegmentRun,
  offsetSourceSegmentAt,
  resolveOffsetRoot,
} from "../model/constructionOffset";
import {
  applyMirror,
  buildMirrorMatrix,
  isMirrorDerived,
  resolveAxisSpec,
  type MirrorMatrix,
} from "../model/constructionMirror";
import {
  resolveRootAllConnections,
  walkMirrorChainToSource,
} from "../model/connections";
import { showCanvasToast } from "../canvasToast";
import {
  isPinnableSnapRef,
  snapRefToConstraintRef,
  MIRROR_OFFSET_REFUSE_HINT,
} from "./constructionSnapRefs";
import {
  RotationController,
  type RotationHost,
  type RotationLiveSnap,
} from "./rotationController";
import {
  DeleteSliceController,
  type DeleteSliceHost,
} from "./deleteSliceController";
import { OffsetController, type OffsetHost } from "./offsetController";
import { ResizeController, type ResizeHost } from "./resizeController";
import {
  ConstructionSnapController,
  type SnapHost,
} from "./constructionSnapController";
import { MirrorController, type MirrorHost } from "./mirrorController";
import { RoundController, type RoundHost } from "./roundController";
import {
  VertexEditController,
  type VertexEditHost,
} from "./vertexEditController";
import {
  SelectEditController,
  type SelectEditHost,
  type SelectDrillState,
} from "./selectEditController";
import {
  DrawSubModeController,
  type DrawSubModeHost,
} from "./drawSubModeController";
import { cssVar } from "../colors";
import {
  isGridSnapActive,
  snapAlongRayToGrid,
  snapToCommonAngle,
} from "./dragSnap";
import { setAngleHint } from "../snapAngleIndicator";
import { findHangingEnd } from "./constructionPrune";
import {
  deleteAnchor as pathDeleteAnchor,
  deleteHandle as pathDeleteHandle,
  deleteSegment as pathDeleteSegment,
  deleteCurves as pathDeleteCurves,
  type SegmentList,
  type SegmentSpec,
} from "./pathDeletion";
import { rotateConstructionLine } from "../model/constructionRotation";
import { type ResizeHandle } from "../model/constructionResize";
import type { EditorContext, ToolSettings } from "./EditorContext";
import { cycleAnchorBezier } from "../model/constructionAnchors";
import {
  measurePathDataLength,
  getPathFirstPoint,
  getPathLastPoint,
  parseSubpathToSegmentList,
  segmentListToPathData,
} from "../model/constructionPathData";
import {
  dropConstructionPrimitive,
  settleSnapReshapedLines,
} from "../model/constructionLineNormalize";
import {
  insertAnchorOnPathData,
  remapAnchorMetaAfterInsert,
} from "./outlineEditGeometry";
import { setCanvasCursor } from "../canvas/canvasCursor";
import { duplicateConstructionSelection } from "../model/constructionGroupActions";

export type ConstructionKind =
  | "polyline"
  | "polygon"
  | "rect"
  | "ellipse"
  | "slot"
  | "line"
  | "freehand";

// Defined in ./constructionSubModes, which has no imports so build-time code
// (the static construction sub-toolbar check, #1230) can use it.
export type { ConstructionSubMode } from "./constructionSubModes";
import type { ConstructionSubMode } from "./constructionSubModes";

/** Offset sub-tools (live + detached) share the same drag gesture. */
export function isOffsetSubMode(m: ConstructionSubMode): boolean {
  return m === "offset" || m === "simple-offset";
}
/** Edit and Delete share the same handle hit-testing, cursor
 *  dispatch, and on-screen handles — Delete is Edit locked to the
 *  delete action, just promoted to its own top-level sub-tool. */
export function isEditOrDeleteSubMode(m: ConstructionSubMode): boolean {
  return m === "edit" || m === "delete";
}
/** Mirror sub-tools (live + detached) share the same click gesture. */
export function isMirrorSubMode(m: ConstructionSubMode): boolean {
  return m === "mirror" || m === "simple-mirror";
}

/** Within Edit, the user picks one of two interaction styles:
 *  - "drag":          the default — anchor / curve / bezier-handle
 *                     dragging.
 *  - "smooth-toggle": tap-only mode where a tap on or near an anchor
 *                     flips its smooth-lock state. Engages a generous
 *                     pickup radius so iPad fingers can reliably hit
 *                     the otherwise-tiny dots. Reset to "drag" each
 *                     time the user enters Edit.
 *  - "add-point":     tap-only mode (#778) where a click on a curve
 *                     inserts a new anchor at that spot WITHOUT
 *                     changing the visible shape (De Casteljau split
 *                     via paper's `divideAt`). */
export type ConstructionEditAction =
  | "drag"
  | "smooth-toggle"
  | "add-point"
  | "delete"
  | "slice";

const PREVIEW_STROKE = () => cssVar("--construction-preview");
/** Stroke colour for the freehand-cut drag preview (slice
 *  sub-sub-tool). Pulled from `--construction-broken` so the
 *  user reads the dashed red line as "this is a destructive
 *  cut" — same red the delete-hover overlay uses. */
const SLICE_CUT_STROKE = () => cssVar("--construction-broken");
/** Default Paper.js `path.simplify(tolerance)` value seeded onto
 *  a freshly-drawn freehand construction line (issue #171). Users
 *  dial it from the properties-panel slider (range 0–10); the
 *  default matches Paper.js's own. Exported so the Draw-tool
 *  settings panel can seed its "Smoothness" preset to the same
 *  value (issue #416). */
export {
  DEFAULT_FREEHAND_SMOOTHNESS,
  MAX_FREEHAND_SMOOTHNESS,
} from "../model/freehandSmoothing";
import {
  currentPointerKind,
  dotHitRadiusMm,
  hitRadiusFor,
} from "../canvas/pointerPrecision";

/**
 * Draws guide / reference shapes that aren't part of the cut pattern.
 * Mirrors the Shape / Cutout tool kind set (rect, ellipse, slot,
 * polygon, freehand) and adds an open-ended `polyline` kind for single
 * guide lines. Construction lines live at the project level and can be
 * drawn on bare canvas — no part required.
 */
export class ConstructionTool
  extends ToolBase
  implements
    RotationHost,
    ResizeHost,
    DeleteSliceHost,
    OffsetHost,
    MirrorHost,
    RoundHost,
    SnapHost,
    VertexEditHost,
    SelectEditHost,
    DrawSubModeHost
{
  id = "construction";
  label = "Construction";
  shortcut = "c";
  cursor = "crosshair";
  // The active draw kind lives on the composed `DrawSubModeController`
  // (#850). This getter keeps `tool.kind` reads working for any caller
  // that references the tool directly; `setKind` / `setSlotWidth`
  // delegate the writes. Default is "rect" (the first tile in the
  // construction kind gallery).
  get kind(): ConstructionKind {
    return this.drawController.kind;
  }
  /** Active sub-tool inside the Construction tool — driven by the
   *  construction sub-toolbar that renders next to the main toolbar
   *  while this tool is selected. */
  subMode: ConstructionSubMode = "draw";
  /** Within the Edit sub-mode, "drag" is the default (anchor / curve /
   *  bezier-handle dragging), "smooth-toggle" engages a tap-only mode
   *  where a tap on or near an anchor flips its smooth-lock state.
   *  Reset to "drag" whenever sub-mode changes — the smooth-toggle
   *  intentionally doesn't persist across sub-mode switches. */
  editAction: ConstructionEditAction = "drag";
  /** Tap point captured on `onDown` while in smooth-toggle edit
   *  action. Cleared when the user drags past the slop threshold
   *  (it was a drag, not a tap), or consumed on `onUp` to find the
   *  nearest anchor and toggle its smooth-lock. */
  private smoothTogglePending: { x: number; y: number } | null = null;
  // Select-sub-mode move + lasso state (`altDuplicatePending` /
  // `lineDrag` / `groupDrag` / `lasso`) moved into `SelectEditController`
  // (#848) — accessed via `this.selectEditController`.
  // #836 — the destructive-edit machinery (delete / slice / prune /
  // swipe-delete / freehand-cut) lives in a composed
  // `DeleteSliceController`. It exclusively owns the four drag-state
  // fields (`deletePending` / `slicePending` / `sliceDrag` /
  // `deleteDrag`, plus their slop-anchor down-points). ConstructionTool
  // owns the controller, delegates the slice/delete cases of
  // onDown/onMove/onUp/onCancel to it, and injects the shared
  // hit-testing / derived-line predicates it needs via the
  // `DeleteSliceHost` interface it implements.
  private deleteSliceController = new DeleteSliceController(this);
  // #837 — the three transform-handle modes each live in a composed
  // controller ConstructionTool owns and drives. Each exclusively owns
  // its ONE drag/hover field (offset drag, mirror hover, round-corner
  // drag) plus that mode's peripheral preset / live-readout state.
  // ConstructionTool delegates the offset/mirror/round cases of
  // onDown/onMove/onHover/onUp/onCancel to them and injects the shared
  // hit-testing / predicates via the Offset/Mirror/RoundHost interfaces
  // it implements.
  private offsetController = new OffsetController(this);
  private mirrorController = new MirrorController(this);
  private roundController = new RoundController(this);
  // #850 — the ENTIRE Draw sub-mode gesture (drag-out rect / ellipse /
  // slot / line / freehand shapes AND the click-to-add polyline /
  // polygon driven by the shared `DrawGestureEngine`) lives in a
  // composed `DrawSubModeController`. It exclusively owns the Draw-mode
  // draft state (`startPt` / `currentPt` / `buffer` / the drag preview),
  // the presets (slot width, rect/ellipse W×H, line length/angle,
  // freehand smoothness), the draw kind, the live-dims / live-line
  // callbacks, and the `DrawGestureEngine` instance (which it drives +
  // implements `DrawGestureHost` for). ConstructionTool owns the
  // controller, delegates the Draw branch of onDown/onMove/onHover/onUp
  // (dispatched last, only while `subMode === "draw"`) to it, and
  // injects the shared scope / scene / project / settings / commit /
  // selection, the `ConstructionSnapController`, and the sibling-gesture
  // reset via the `DrawSubModeHost` interface it implements.
  private drawController = new DrawSubModeController(this);
  /** Fired every frame during a vertex drag of a Line-primitive
   *  construction line so the per-line properties panel can mirror
   *  the live geometry in its Length / Angle inputs without waiting
   *  for the drag-commit. Same visual-degrees convention as
   *  `liveLineCallback`. */
  private liveLinePrimitiveCallback?: (
    lengthMm: number,
    angleDeg: number,
  ) => void;
  /** Per-frame resize live-update (issue #388) — pushes the in-progress
   *  (uncommitted) construction line to the properties panel so its dimension
   *  readouts track the drag. Fired with null on drag end. */
  private liveResizeCallback?: (line: ConstructionLine | null) => void;

  // Draw-mode drag-state (`startPt` / `currentPt` / `buffer` /
  // `previewItem`), the modifier-replay capture (`lastDrawSample` /
  // `lastDrawP`), and the `DrawGestureEngine` moved into the composed
  // `DrawSubModeController` (#850) — accessed via `this.drawController`.
  /** Last hover point seen in an Edit sub-sub-tool, so a bare
   *  Shift press / release can re-run `refreshEditCursor` and flip
   *  the Delete highlight between per-target and whole-line without
   *  needing a mouse move (issue #670 follow-up). */
  private lastEditP: { x: number; y: number } | null = null;

  // #835 — the rotation machinery (line / image / group rotation + the
  // draggable pivot crosshair) lives in a composed `RotationController`.
  // ConstructionTool owns it, drives it from the select-mode branches of
  // onDown/onMove/onHover/onUp/onCancel, and injects every collaborator
  // it needs (scope/scene/settings/project, the shared live-snap
  // machinery, the resize interplay, and the other concerns' drag
  // guards) via the `RotationHost` interface it implements.
  private rotationController = new RotationController(this);

  // Corner-resize handles (#388) — the resize drag state + hit-testing /
  // preview / commit machinery live in a composed `ResizeController`
  // (#838). ConstructionTool owns it, drives it from the select-mode
  // branches of onDown/onMove/onUp/onCancel, and injects its
  // collaborators via the `ResizeHost` interface it implements. Its
  // hit-test lookups (`resizeHandleAt` / `resizeCursorFor` /
  // `resizableSelectedLineId`) also back the RotationHost resize hooks.
  private resizeController = new ResizeController(this);

  // Construction snap tiers (#839) — the live/draft snap STATE
  // (liveSnap / draftSnaps / draftHoverSnap) plus every snap resolver
  // (radius, live/draft anchor snap + overlay, grid #259, line-crossing
  // #323, angle predicates) live in a composed `ConstructionSnapController`.
  // ConstructionTool owns it, delegates its RotationHost / DrawGestureHost
  // snap hooks to it, and injects the four core collaborators via the
  // `SnapHost` interface it implements. This is the shared foundation the
  // other controllers and the draw engine already snap through.
  private snapController = new ConstructionSnapController(this);

  // #846 — the Edit-sub-mode direct-manipulation drags (vertex / bezier
  // handle / curve bend) live in a composed `VertexEditController`. It
  // exclusively owns the three drag-state fields (`vertexDrag` /
  // `bezierHandleDrag` / `curveDrag`) plus the drag-exclusive geometry
  // helpers. ConstructionTool owns the controller, delegates the
  // edit-mode start / apply / commit thirds of onDown/onMove/onUp/onCancel
  // to it, and injects the shared snap tiers, mirror routing,
  // `translateDragClosure`, live readouts + cursor refresh via the
  // `VertexEditHost` interface it implements.
  private vertexEditController = new VertexEditController(this);

  // #848 — the Select-sub-mode whole-object move drags (single line /
  // group / bbox / ad-hoc multi), the deferred Alt-drag clone arming
  // (#345), and the marquee/lasso gesture live in a composed
  // `SelectEditController`. It exclusively owns the four interaction-
  // state fields (`lineDrag` / `groupDrag` / `altDuplicatePending` /
  // `lasso`) plus the drag/lasso-exclusive helpers. ConstructionTool
  // owns the controller, delegates the select-mode pick / apply /
  // commit thirds of onDown/onMove/onUp/onCancel to it, and injects the
  // shared snap tiers, angle-lock + drag guide, mirror routing,
  // `translateDragClosure`, the lasso hit-test, the drill-selection
  // state, and the duplicate action via the `SelectEditHost` interface
  // it implements.
  private selectEditController = new SelectEditController(this);

  /** Test hook: current Mirror sub-tool hover axis (or `null` if no
   *  candidate). Exposed via `__editor.getMirrorHoverAxis` so e2e
   *  specs can assert which axis the cursor's resolving. Forwards to
   *  the composed `MirrorController` (#837). */
  getMirrorHoverAxis():
    | NonNullable<ConstructionLine["mirrorSource"]>["axis"]
    | null {
    return this.mirrorController.getMirrorHoverAxis();
  }

  /** Single-segment offset mode (issue #681). When on, an Offset drag
   *  targets just the ONE edge under the cursor and commits a straight
   *  line parallel to it, instead of offsetting the whole outline. Set
   *  two ways, OR'd together per drag: the persistent panel toggle
   *  (`singleSegmentOffset`) and a held Alt/Option at pointer-down.
   *  Whole-shape offset stays the default. */
  private singleSegmentOffset = false;

  // The draw presets + kind live on the composed `DrawSubModeController`
  // (#850); these keep their EXACT external signatures for app.ts and
  // delegate the body into the controller.
  setKind(kind: ConstructionKind) {
    this.drawController.setKind(kind);
  }

  setSlotWidth(width: number) {
    this.drawController.setSlotWidth(width);
  }

  setPresetWidth(w: number | null) {
    this.drawController.setPresetWidth(w);
  }

  setPresetHeight(h: number | null) {
    this.drawController.setPresetHeight(h);
  }

  setPresetLineLength(mm: number | null) {
    this.drawController.setPresetLineLength(mm);
  }

  setPresetLineAngleDeg(deg: number | null) {
    this.drawController.setPresetLineAngleDeg(deg);
  }

  /** Corner radius the next rectangle is drawn with (#1069). */
  setDrawCornerRadius(mm: number | null) {
    this.drawController.setDrawCornerRadius(mm);
  }

  /** Set the default smoothness applied to the next freehand line
   *  (issue #416). Clamped to the same 0–10 range the per-line
   *  slider exposes. */
  setPresetFreehandSmoothness(v: number) {
    this.drawController.setPresetFreehandSmoothness(v);
  }

  setLiveDimsCallback(fn: ((w: number, h: number) => void) | undefined) {
    this.drawController.setLiveDimsCallback(fn);
  }

  setLiveLineCallback(
    fn: ((lengthMm: number, angleDeg: number) => void) | undefined,
  ) {
    this.drawController.setLiveLineCallback(fn);
  }

  setLiveLinePrimitiveCallback(
    fn: ((lengthMm: number, angleDeg: number) => void) | undefined,
  ) {
    this.liveLinePrimitiveCallback = fn;
  }

  /** Wire the per-frame resize live-update (issue #388). Fired during a corner
   *  resize with the in-progress (uncommitted) line so the properties panel's
   *  dimension readouts track the drag; fired with null on drag end. */
  setLiveResizeCallback(
    fn: ((line: ConstructionLine | null) => void) | undefined,
  ) {
    this.liveResizeCallback = fn;
  }

  /** Set / clear the offset sub-mode's typed distance. When set,
   *  the next click on a construction line commits an offset of
   *  this magnitude — the cursor's position relative to the line
   *  at down picks the sign. `null` falls back to drag-defined
   *  distance. */
  setPresetOffsetDistance(mm: number | null) {
    this.offsetController.setPresetOffsetDistance(mm);
  }

  setLiveOffsetDistanceCallback(fn: ((distance: number) => void) | undefined) {
    this.offsetController.setLiveOffsetDistanceCallback(fn);
  }

  /** Rounded-corner preference for the next offset (issue #423) — the
   *  tool-side twin of the derived line's own "Round corners"
   *  checkbox, so the user sets the shape before drawing it. */
  setOffsetRoundedCorners(on: boolean) {
    this.offsetController.setRoundedCorners(on);
  }

  /** Single-segment offset toggle (issue #681). When on, an Offset
   *  drag follows just the ONE edge under the cursor instead of the
   *  whole outline. Holding Alt/Option during the drag does the same
   *  per-gesture. */
  getSingleSegmentOffset(): boolean {
    return this.singleSegmentOffset;
  }

  setSingleSegmentOffset(on: boolean) {
    this.singleSegmentOffset = on;
  }

  /** Set / clear the round sub-mode's preset corner radius. When
   *  set, the next click on a sharp corner commits at this radius
   *  without requiring a drag. `null` falls back to drag-defined
   *  radius. */
  setPresetCornerRadius(mm: number | null) {
    this.roundController.setPresetCornerRadius(mm);
  }

  setLiveCornerRadiusCallback(fn: ((mm: number) => void) | undefined) {
    this.roundController.setLiveCornerRadiusCallback(fn);
  }

  /** Called from app.ts when the snap toolbar's effective round
   *  mode flips (toggle clicked OR Ctrl/Cmd state changed) while
   *  the Construction tool is in Round sub-mode. Forwards to the
   *  composed `RoundController` (#837), which refreshes the in-flight
   *  drag preview so the previewed geometry picks up the new effective
   *  mode without waiting for the next mouse-move. */
  notifyRoundModeChanged(): void {
    this.roundController.notifyRoundModeChanged();
  }

  /** Begin assembling a new constraint of `kind` whose first
   *  participant is the construction line `originLineId`. Subsequent
   *  taps on the canvas capture refs (anchor / curve / line) until
   *  the kind's required count is reached, at which point the
   *  picker auto-commits and clears. The Construction tool flips
   *  itself into Edit sub-mode so anchor handles render and become
   *  clickable targets. */
  startConstraintPicker(kind: ConstraintKind, originLineId: string): void {
    // Kill any in-flight drag / draft so the picker takes over a
    // clean slate.
    this.cancelDraft();
    this.selectEditController.reset();
    this.vertexEditController.reset();
    this.rotationController.imageRotationDrag = null;
    this.smoothTogglePending = null;
    this.deleteSliceController.reset();
    this.ctx.scene.setConstructionDeleteHover(null);
    // Force into Edit sub-mode (handles visible) so the user can tap
    // anchors. Reset edit action to "drag" so our picker short-circuit
    // in onDown is the only thing handling clicks.
    this.subMode = "edit";
    this.editAction = "drag";
    this.ctx.scene.setShowConstructionHandles(true);
    this.constraintPicker = { kind, refs: [], originLineId };
    this.notifyConstraintPickerListeners();
  }

  /** Drop the in-progress picker without committing. Called by ESC,
   *  by panel "Cancel" buttons, and by sub-mode switches that imply
   *  the user has moved on. */
  cancelConstraintPicker(): void {
    if (!this.constraintPicker) return;
    this.constraintPicker = null;
    // Drop the yellow overlay that was visualising the picked refs.
    this.ctx.scene.setConstructionConstraintHover(null);
    this.notifyConstraintPickerListeners();
  }

  /** Read-only snapshot of the picker state. Returns null when no
   *  picker is active. */
  getConstraintPicker(): {
    kind: ConstraintKind;
    refs: ReadonlyArray<ConstraintRef>;
    originLineId: string;
  } | null {
    if (!this.constraintPicker) return null;
    return {
      kind: this.constraintPicker.kind,
      refs: this.constraintPicker.refs,
      originLineId: this.constraintPicker.originLineId,
    };
  }

  /** Subscribe to picker state changes. Returns an unsubscribe fn. */
  addConstraintPickerListener(fn: () => void): () => void {
    this.constraintPickerListeners.add(fn);
    return () => this.constraintPickerListeners.delete(fn);
  }

  private notifyConstraintPickerListeners(): void {
    for (const fn of this.constraintPickerListeners) fn();
  }

  /** Live-mirror coincident-constraint partners during a drag.
   *  Walks each coincident constraint, picks a "source" anchor
   *  that's part of the active drag, reads its current rendered
   *  position, and snaps every other anchor ref on a *different*
   *  line to that position. Operates only on the rendered scene —
   *  pathData isn't touched until the drag commits, where the
   *  same propagation runs again on the model. Visible result:
   *  partner anchors track the dragged geometry frame-by-frame
   *  instead of jumping into place on release.
   *
   *  `source` discriminates how to find each constraint's source:
   *    - "anchor": only the constraint refs that exactly match
   *      the dragged anchor count as the source (so dragging the
   *      *partner* anchor of an existing pair correctly snaps the
   *      *original* anchor to the cursor).
   *    - "line":  any anchor ref on the moved line counts as a
   *      source — every anchor on the line shifted by the same
   *      translation, so each constraint reads its on-line
   *      anchor's new position and snaps off-line partners. */
  /** Translate every line in a pre-computed drag closure by the
   *  current frame's delta. Replaces the legacy per-frame BFS over
   *  snap + offset edges: the closure is computed once at mouseDown
   *  via `computeDragClosure` (or `computeVertexDragClosure` for
   *  vertex drags), stored on the drag state, and this method just
   *  iterates the same set every frame.
   *
   *  `originIds` lists lines the caller has already translated this
   *  frame (the dragged origin for line drag, every group member for
   *  group drag) — they're skipped here to avoid double-translating.
   *  For vertex drag the dragged line is excluded from the closure
   *  itself, and `originIds` is empty.
   *
   *  Crosshair markers re-read live segment positions, so a single
   *  refresh after the translate loop keeps them in sync with the
   *  moved partners. */
  /** The object's TRANSLATION for an angle-locked drag: the raw cursor move
   *  (`start`→`cursor`) snapped to the nearest common drafting angle, then —
   *  with grid-snap on — adjusted so the grabbed reference point `ref` slides
   *  along that ray onto the nearest grid-line crossing, so the object lands on
   *  a grid position by the corner/point you grabbed (#725).
   *
   *  SelectEditHost: shared snap machinery injected into the composed
   *  SelectEditController's move drags (#848). */
  angleGridTranslation(
    start: { x: number; y: number },
    cursor: { x: number; y: number },
    ref: { x: number; y: number },
  ): { tx: number; ty: number } {
    const snapped = snapToCommonAngle(start, cursor);
    let end = {
      x: ref.x + (snapped.x - start.x),
      y: ref.y + (snapped.y - start.y),
    };
    if (isGridSnapActive(this.ctx.settings)) {
      const grid = this.ctx.scene.getMatGridInfo();
      const g = grid ? snapAlongRayToGrid(ref, end, grid) : null;
      if (g) end = g;
    }
    return { tx: end.x - ref.x, ty: end.y - ref.y };
  }

  /** Show the angle-snap guide (pivot dot + directional ray + endpoint
   *  marker + distance label) + HUD angle chip for the current drag frame
   *  (#725); hidden until the cursor has moved off the grab point so a
   *  click doesn't flash a guide.
   *
   *  SelectEditHost: shared drag-guide rendering injected into the
   *  composed SelectEditController's move drags (#848). */
  updateDragGuide(
    from: { x: number; y: number },
    to: { x: number; y: number },
    rawMoved: number,
    angleActive: boolean,
  ): void {
    if (rawMoved > 0.2) {
      this.ctx.scene.setDragAxisGuide(from, to);
      // HUD angle chip only while angle-snapping.
      setAngleHint(
        angleActive ? Math.atan2(to.y - from.y, to.x - from.x) : null,
      );
    } else {
      this.clearDragAngleGuide();
    }
  }

  /** SelectEditHost: hide the angle-snap guide + HUD angle chip. */
  clearDragAngleGuide(): void {
    this.ctx.scene.clearDragAxisGuide();
    setAngleHint(null);
  }

  translateDragClosure(
    closure: ReadonlySet<string>,
    originIds: ReadonlySet<string>,
    dx: number,
    dy: number,
  ): void {
    if (dx === 0 && dy === 0) return;
    if (closure.size === 0) return;
    for (const id of closure) {
      if (originIds.has(id)) continue;
      this.ctx.scene.translateConstructionLineRender(id, dx, dy);
      this.ctx.scene.refreshConstructionHandles(id);
      // Live MIRROR update only — NOT a full connection-rederive.
      // Snap-chain partners include offset descendants (the
      // closure walks the offset adjacency), so they're already
      // being translated rigidly by this loop. Calling
      // `rederiveOffsetsOf(id)` here would compute the offset's
      // path from the just-translated source and write it on top
      // of the already-translated offset item — a 2× delta on C
      // (regression covered by `offset.spec.ts:967`). Mirrors are
      // safe to re-derive because they aren't in the rigid-
      // translation closure today (mirror-derived lines can be
      // snap targets but they re-derive from the source + axis
      // rather than translating rigidly).
      this.ctx.scene.rederiveMirrorsAffectedBy(id);
    }
    this.ctx.scene.refreshConstructionCrosshairsLive();
  }

  /** Resolve the most-specific construction-line feature under the
   *  pointer into a `ConstraintRef`. Anchor wins over curve wins
   *  over whole line; bezier-handle endpoints aren't valid
   *  constraint targets (they're a tangent gesture, not geometry).
   *  Falls back to the touch-friendly anchor pickup so an iPad tap
   *  near an anchor still counts.
   *
   *  When called during a constraint pick, candidates whose ref
   *  kind isn't allowed for the active constraint kind are skipped
   *  — so e.g. clicking on a curve while picking coincident (which
   *  is anchor-only) still falls through to the touch-friendly
   *  anchor lookup, and only refuses if no anchor is nearby
   *  either. */
  private resolveConstraintRef(pt: paper.Point): ConstraintRef | null {
    const allowed: ReadonlyArray<"anchor" | "curve" | "line"> = this
      .constraintPicker
      ? allowedRefKindsForConstraint(this.constraintPicker.kind)
      : ["anchor", "curve", "line"];
    const hit = this.ctx.scene.hitTest(pt);
    if (allowed.includes("anchor")) {
      const handle = this.findVisibleHandleHit(pt);
      if (handle && handle.kind === "anchor") {
        return {
          kind: "anchor",
          constructionId: handle.constructionId,
          pathIndex: handle.pathIndex,
          segmentIndex: handle.segmentIndex,
        };
      }
    }
    if (allowed.includes("curve")) {
      const curveHit = this.hitConstructionCurve(pt);
      if (curveHit) {
        return {
          kind: "curve",
          constructionId: curveHit.constructionId,
          pathIndex: curveHit.pathIndex,
          curveIndex: curveHit.curveIndex,
        };
      }
    }
    if (allowed.includes("line") && hit.constructionId) {
      return { kind: "line", constructionId: hit.constructionId };
    }
    return null;
  }

  /** Commit the assembled picker as a new project-level Constraint
   *  and clear the picker state. Listeners are notified after the
   *  commit so the panel can re-render against the new history
   *  state (which now contains the constraint).
   *
   *  Coincident constraints also enforce themselves in the same
   *  commit: the first picked ref is the "source" (the user's
   *  first click is the reference position) and every other
   *  anchor ref snaps to it. Otherwise creating a coincident
   *  feels inert until the user does a separate drag. */
  private commitConstraintPicker(): void {
    const picker = this.constraintPicker;
    if (!picker) return;
    const captured = { kind: picker.kind, refs: [...picker.refs] };
    this.constraintPicker = null;
    // Clear the picker's pinned overlay before committing — the
    // commit re-renders the scene, and the new constraint will get
    // its own hover overlay if the user hovers the matching row.
    this.ctx.scene.setConstructionConstraintHover(null);
    const scope = this.ctx.scene.getPaperScope();
    this.ctx.commit("Add constraint", (proj) => {
      const next: Project = {
        ...proj,
        constraints: [
          ...(proj.constraints ?? []),
          { id: newId(), kind: captured.kind, refs: captured.refs },
        ],
      };
      // Immediate Snap enforcement — treat the first ref's current
      // position as the "just-dragged" anchor so the propagator
      // rigidly translates every connected partner line onto it.
      // Skips when the first ref isn't an anchor (line / curve refs
      // belong to other constraint kinds that don't get enforcement
      // yet).
      const firstRef = captured.refs[0];
      if (
        captured.kind === "coincident" &&
        firstRef &&
        firstRef.kind === "anchor"
      ) {
        const enforced = propagateCoincidentAfterAnchorDrag(
          next,
          next.constructionLines ?? [],
          {
            constructionId: firstRef.constructionId,
            pathIndex: firstRef.pathIndex,
            segmentIndex: firstRef.segmentIndex,
          },
          scope,
        );
        return settleSnapReshapedLines(
          next,
          next.constructionLines ?? [],
          enforced,
          scope,
        );
      }
      return next;
    });
    this.notifyConstraintPickerListeners();
  }

  setSubMode(mode: ConstructionSubMode) {
    if (this.subMode === mode) return;
    // Bail out of any in-progress draft / drag before switching modes
    // so a half-drawn line or open vertex / curve drag doesn't bleed
    // into the new sub-tool.
    this.cancelDraft();
    this.selectEditController.reset();
    this.vertexEditController.reset();
    this.rotationController.imageRotationDrag = null;
    this.clearLiveSnap();
    // Sub-sub-tool state lives only inside Edit — reset to plain
    // drag every time the sub-mode changes so the user always
    // re-enters Edit ready to drag handles, never silently still
    // in the smooth-toggle tap mode from a previous visit.
    this.editAction = "drag";
    this.smoothTogglePending = null;
    this.deleteSliceController.reset();
    // Drop the red delete-hover overlay if it was up — sub-mode
    // change exits delete entirely.
    this.ctx.scene.setConstructionDeleteHover(null);
    // Drop the Offset hover affordance too — it only lives inside the
    // Offset sub-mode (issue #148).
    this.ctx.scene.setOffsetHoverAffordance(null);
    // Sub-mode switch is also an implicit "I changed my mind" for
    // any in-progress constraint pick.
    this.cancelConstraintPicker();
    // Drop the drill-click state on sub-mode change — the next click
    // in the new sub-mode should start a fresh drill sequence.
    this.drillState = null;
    // Mirror-hover overlay only lives inside the Mirror sub-mode.
    this.mirrorController.clear();
    this.subMode = mode;
    // Handles render in Edit and Delete (Delete targets the same
    // anchors / handles) — re-sync the scene so they appear /
    // disappear immediately on mode change.
    this.ctx.scene.setShowConstructionHandles(isEditOrDeleteSubMode(mode));
    // Corner-rounding handles only render in round mode (#100).
    this.ctx.scene.setShowCornerHandles(mode === "round");
    // Rotation overlay only in select mode (#67).
    this.ctx.scene.setShowConstructionRotationHandles(mode === "select");
    if (mode !== "select") {
      this.ctx.scene.setLineRotationHoverCorner(null);
      this.ctx.scene.setGroupRotationHoverCorner(null);
      this.ctx.scene.setTransientLineRotation(null);
      this.ctx.scene.setTransientGroupRotation(null);
    }
    setAngleHint(null);
  }

  /** True in the Edit or Delete sub-mode — they share the same
   *  handle hit-testing and cursor dispatch. */
  private isEditOrDelete(): boolean {
    return isEditOrDeleteSubMode(this.subMode);
  }

  /** The interaction the Edit-style handlers should run. In the
   *  dedicated Delete sub-mode it's always "delete"; otherwise it's
   *  whichever Edit sub-sub-tool the user picked. */
  private effectiveEditAction(): ConstructionEditAction {
    return this.subMode === "delete" ? "delete" : this.editAction;
  }

  /** Toggle the in-Edit interaction style. Only meaningful while the
   *  sub-mode is "edit" — `setSubMode` clears this back to "drag" on
   *  every mode change, so callers can fire it freely. */
  setEditAction(action: ConstructionEditAction) {
    if (this.editAction === action) return;
    // Drop any pending tap if the user switches mid-press.
    this.smoothTogglePending = null;
    this.deleteSliceController.reset();
    // Switching away from delete clears its hover overlay; the
    // refreshEditCursor that fires on the next pointer move will
    // re-establish it if the user returns to delete.
    if (this.editAction === "delete") {
      this.ctx.scene.setConstructionDeleteHover(null);
    }
    this.editAction = action;
  }

  /** Fire `liveLinePrimitiveCallback` with the live length + visual
   *  angle of `constructionId`'s rendered path, when (a) a callback
   *  is wired and (b) the line is still a Line primitive. Used by
   *  the vertex-drag onMove so the per-line panel's Length / Angle
   *  inputs track the dragged geometry in real time. Reads the live
   *  paper item (mutated each frame by the drag) so the values
   *  reflect the in-progress shape, not the pre-drag model
   *  snapshot. No-ops when the line lost its primitive earlier in
   *  the drag (e.g. cycled into a curve). */
  notifyLinePrimitiveLiveUpdate(
    constructionId: string,
    scope: paper.PaperScope,
  ): void {
    if (!this.liveLinePrimitiveCallback) return;
    const line = projectConstructionLines(this.ctx.history.state).find(
      (l) => l.id === constructionId,
    );
    if (line?.primitive?.kind !== "line") return;
    const item = this.ctx.scene.getConstructionLineItem(constructionId);
    if (!item) return;
    // Reparse the live pathData rather than walking the item's
    // children — the item could be either a Path or a CompoundPath
    // depending on how the renderer built it, and re-parsing keeps
    // this helper agnostic to that.
    let cp: paper.CompoundPath | null = null;
    try {
      cp = new scope.CompoundPath({
        pathData: (item as paper.PathItem).pathData,
        insert: false,
      });
    } catch {
      return;
    }
    const subpaths: paper.Path[] =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [cp as unknown as paper.Path];
    const sub = subpaths[0];
    if (!sub || sub.segments.length < 2) {
      cp.remove();
      return;
    }
    const p1 = sub.segments[0]!.point;
    const p2 = sub.segments[1]!.point;
    cp.remove();
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const length = Math.hypot(dx, dy);
    let visualDeg = (Math.atan2(-dy, dx) * 180) / Math.PI;
    if (visualDeg < 0) visualDeg += 360;
    this.liveLinePrimitiveCallback(length, visualDeg);
  }

  // Vertex-edit drag state (`vertexDrag`) moved into
  // `VertexEditController` (#846) — accessed via `this.vertexEditController`.

  // Move-line drag state (`lineDrag`), whole-group drag state
  // (`groupDrag`), and the deferred Alt-drag clone arming
  // (`altDuplicatePending`) moved into `SelectEditController` (#848) —
  // accessed via `this.selectEditController`.

  /** Drill state for the canvas-click selection model. A plain click
   *  on a construction line resolves to the **outermost** group
   *  containing it; consecutive clicks on the same line (the
   *  double-tap / triple-tap pattern in Figma/Sketch) drill down one
   *  ancestor group per click, eventually selecting the line itself
   *  for very nested members. Anywhere the click target's chain
   *  doesn't share the current drill anchor, the depth resets to 0
   *  so the next click selects the new line's outermost group
   *  instead of carrying drill state from an unrelated subtree.
   *
   *  Tracked: the line we're drilling against (`lineId`), the depth
   *  we resolved to on the last click (`depth`), the timestamp so
   *  we can window samesite continuation, and `wasDrag` so a click
   *  that became an actual drag isn't treated as the "1st of a
   *  double-click." Set to null on tool deactivation / sub-mode
   *  change. */
  private drillState: SelectDrillState | null = null;

  // Lasso-select drag state (`lasso`) and the drill-window /
  // drag-threshold constants moved into `SelectEditController` (#848) —
  // the marquee gesture is owned there; `drillState` stays on the tool
  // (the shared canvas-click selection model) and is read / written by
  // the controller through the host.

  /** Resolve the set of member lines to drag together when the user
   *  grabs `hitLineId` while one or more groups are selected. The
   *  hit line must be a descendant of *some* selected group; the
   *  drag set is every descendant line of that group (which already
   *  includes any nested child groups via `descendantConstructionLineIds`).
   *
   *  Returns null when the hit line isn't a member of any selected
   *  group, so the caller falls back to single-line drag. We pick
   *  the **deepest** matching group so a click on a leaf inside a
   *  nested subtree drags just that subtree rather than the whole
   *  outer wrapper. */
  // `expandSelectionToObjectDragSet` moved into `SelectEditController`
  // (#848) — accessed via `this.selectEditController`.

  private findGroupMembershipFor(
    hitLineId: string,
    selectedGroupIds: readonly string[],
  ): { groupId: string; lineIds: string[] } | null {
    const proj = this.ctx.history.state;
    const allGroups = projectConstructionGroups(proj);
    const byId = new Map(allGroups.map((g) => [g.id, g] as const));
    let best: { id: string; depth: number; lineIds: string[] } | null = null;
    for (const gid of selectedGroupIds) {
      if (!byId.has(gid)) continue;
      const lineIds = descendantConstructionLineIds(proj, gid);
      if (!lineIds.includes(hitLineId)) continue;
      // Depth = number of ancestor groups; deeper wins so nested
      // selections behave intuitively.
      let depth = 0;
      let cursor: string | undefined = gid;
      while (cursor) {
        depth++;
        const parent = allGroups.find((g) =>
          (g.groupIds ?? []).includes(cursor!),
        );
        cursor = parent?.id;
        if (depth > 1000) break; // Defensive — invariants forbid cycles.
      }
      if (!best || depth > best.depth) {
        best = { id: gid, depth, lineIds };
      }
    }
    return best ? { groupId: best.id, lineIds: best.lineIds } : null;
  }

  // `deriveDrillDepthFromSelection` and `resolveGroupDragSourcePos`
  // moved into `SelectEditController` (#848) — accessed via
  // `this.selectEditController`.

  // Rotation gesture geometry constants (ROTATION_* / PIVOT_*) moved
  // into `RotationController` (#835) alongside the machinery that uses
  // them.

  /** Returns the id of the single construction line currently
   *  selected (and rotatable — i.e., not a derived offset), or null.
   *  Mirrors Scene.singleSelectedConstructionLineId but adds the
   *  "no offset" guard the gesture needs since offsets aren't
   *  directly editable. */
  rotatableSelectedLineId(): string | null {
    const id = this.ctx.scene.singleSelectedConstructionLineId();
    if (!id) return null;
    const line = projectConstructionLines(this.ctx.history.state).find(
      (l) => l.id === id,
    );
    if (!line) return null;
    if (line.offsetSource) return null;
    return id;
  }

  // ---- RotationHost implementation (#835) ---------------------------
  // ConstructionTool owns a `RotationController` (`this.rotationController`)
  // that runs the line / image / group rotation gestures + the draggable
  // pivot crosshair. These hooks inject the collaborators it needs.
  // `getScope` / `getScene` are shared with the DrawGestureHost impl
  // below; the resize-interplay + live-snap hooks are also used by the
  // tool's own non-rotation gestures.

  getSettings(): ToolSettings {
    return this.ctx.settings;
  }
  getProject(): Project {
    return this.ctx.history.state;
  }
  /** DeleteSliceHost (#1013): the knife's snap pipeline reads the snap
   *  settings, the mat grid and the project's snappable geometry. */
  getContext(): EditorContext {
    return this.ctx;
  }
  commit(label: string, mutate: (proj: Project) => Project): void {
    this.ctx.commit(label, mutate);
  }
  rerender(): void {
    this.ctx.rerender();
  }
  /** OffsetHost hook — true in the detached "simple offset" sub-mode
   *  (issue #344), where the commit produces a standalone line. */
  isSimpleOffset(): boolean {
    return this.subMode === "simple-offset";
  }
  /** MirrorHost hook — true in the detached "simple mirror" sub-mode
   *  (issue #344), where the commit bakes then strips the link. */
  isSimpleMirror(): boolean {
    return this.subMode === "simple-mirror";
  }
  /** DeleteSliceHost / SelectEditHost hook — set the scene selection
   *  (post-commit selection after a delete / slice / prune, and the
   *  select-mode pick resolution). The optional `opts` forwards the
   *  `autoPromoteGroups` flag the drill selection relies on (#848). */
  setSelection(
    sel: SceneSelection,
    opts?: { autoPromoteGroups?: boolean },
  ): void {
    this.ctx.setSelection(sel, opts);
  }
  /** SelectEditHost hook — clone the current selection for an Alt-drag
   *  duplicate (#345). Forwards to the shared model action. */
  duplicateConstructionSelection(opts: {
    offsetMm?: number;
  }): { lineIds: string[]; groupIds: string[]; imageIds: string[] } | null {
    return duplicateConstructionSelection(this.ctx, opts);
  }
  /** SelectEditHost hook — read the shared drill-selection state
   *  (owned by the tool, driven by the SelectEditController). */
  getDrillState(): SelectDrillState | null {
    return this.drillState;
  }
  /** SelectEditHost hook — write the shared drill-selection state. */
  setDrillState(state: SelectDrillState | null): void {
    this.drillState = state;
  }
  /** RotationHost: id of the single selected non-offset line currently
   *  showing corner RESIZE handles (#388), or null — its corner ring is
   *  given over to resize, so corner rotation skips it. Delegates to the
   *  ResizeController (#838), which owns the resize hit-testing now. */
  resizableSelectedLineId(): string | null {
    return this.resizeController.resizableSelectedLineId();
  }
  /** RotationHost: which resize handle (corner or edge, #863) `p` falls on
   *  for the selected line, or null — the line rotation hover yields to
   *  resize. Delegates to the ResizeController (#838). */
  resizeHandleAt(p: { x: number; y: number }): { handle: ResizeHandle } | null {
    return this.resizeController.resizeHandleAt(p);
  }
  /** RotationHost: resize cursor for a handle (corner or edge). Delegates to
   *  the ResizeController (#838). */
  resizeCursorFor(handle: ResizeHandle): string {
    return this.resizeController.resizeCursorFor(handle);
  }
  isResizeDragActive(): boolean {
    return this.resizeController.isResizeDragActive();
  }
  isLineDragActive(): boolean {
    return this.selectEditController.isLineDragActive();
  }
  isGroupDragActive(): boolean {
    return this.selectEditController.isGroupDragActive();
  }
  isLassoActive(): boolean {
    return this.selectEditController.isLassoActive();
  }
  getLiveSnap(): RotationLiveSnap | null {
    return this.snapController.getLiveSnap();
  }
  setLiveSnap(snap: RotationLiveSnap | null): void {
    this.snapController.setLiveSnap(snap);
  }

  /** Apply a specific absolute rotation (degrees) to one construction
   *  line — delegates to the rotation controller. Public because it is
   *  the properties-panel rotation scrubber's entry point (issue #67). */
  applyConstructionLineRotation(lineId: string, targetDeg: number): void {
    this.rotationController.applyConstructionLineRotation(lineId, targetDeg);
  }

  // `beginAltDuplicateDrag` (#345) and `startGroupDrag` moved into
  // `SelectEditController` (#848) — accessed via `this.selectEditController`.

  // Live/draft snap tiers (#839) are owned by `this.snapController`
  // (ConstructionSnapController): the liveSnap / draftSnaps /
  // draftHoverSnap state plus every resolver moved there. ConstructionTool
  // keeps thin public delegators for the RotationHost snap hooks
  // (`snapRadiusMm` / `clearLiveSnap` / `refreshLiveSnapOverlay`;
  // `getLiveSnap` / `setLiveSnap` sit with the other RotationHost hooks
  // above) and calls the controller directly everywhere else.

  /** RotationHost + shared: project-mm distance for the pixel snap
   *  radius (inverse-zoom). Delegates to the snap controller (#839). */
  snapRadiusMm(): number {
    return this.snapController.snapRadiusMm();
  }

  /** RotationHost + shared: drop the live drag-snap pair and its scene
   *  highlight. Delegates to the snap controller (#839). */
  clearLiveSnap(): void {
    this.snapController.clearLiveSnap();
  }

  /** RotationHost + shared: re-render the snap-candidate hover overlay
   *  from the current live-snap state. Delegates to the snap
   *  controller (#839). */
  refreshLiveSnapOverlay(): void {
    this.snapController.refreshLiveSnapOverlay();
  }

  /** VertexEditHost: per-frame anchor snap during a vertex drag.
   *  Delegates to the snap controller (#839 / #846). */
  updateLiveSnap(
    sources: Array<{ ref: SnapRefLite; pos: { x: number; y: number } }>,
    extraExcludedLineIds?: ReadonlySet<string>,
  ): { sourceRef: SnapRefLite; candidatePos: { x: number; y: number } } | null {
    return this.snapController.updateLiveSnap(sources, extraExcludedLineIds);
  }

  /** VertexEditHost: land the dragged vertex on a nearby construction
   *  edge (#323). Delegates to the snap controller (#839 / #846). */
  findLineSnap(
    p: { x: number; y: number },
    excludeId?: string,
  ): { x: number; y: number } | null {
    return this.snapController.findLineSnap(p, excludeId);
  }

  /** VertexEditHost: nearest crossing of two visible lines (#128). */
  findIntersectionSnap(
    p: { x: number; y: number },
    excludeId?: string,
  ): { x: number; y: number } | null {
    return this.snapController.findIntersectionSnap(p, excludeId);
  }

  /** ResizeHost: snap a dragged resize handle against every enabled snap
   *  (#1169). Delegates to the snap controller. */
  snapHandleDrag(
    p: { x: number; y: number },
    excluded: ReadonlySet<string>,
  ): {
    point: { x: number; y: number };
    candidate: SnapRefLite | null;
  } | null {
    return this.snapController.snapHandleDrag(p, excluded);
  }

  /** ResizeHost: remove the handle-drag snap indicator. */
  clearHandleDragSnap(): void {
    this.snapController.clearHandleDragSnap();
  }

  /** VertexEditHost: capture a smooth-toggle pending tap. The
   *  smooth-toggle gesture lives on the tool; an anchor press in that
   *  mode defers to it rather than starting a vertex drag (#846). */
  setSmoothTogglePending(p: { x: number; y: number }): void {
    this.smoothTogglePending = { x: p.x, y: p.y };
  }

  // Bezier-handle drag state (`bezierHandleDrag`) and curve-bend drag
  // state (`curveDrag`) moved into `VertexEditController` (#846) —
  // accessed via `this.vertexEditController`.

  /** Active "add constraint" picker. Started from a palette-chip
   *  click in the Properties panel; the panel sets this via
   *  `startConstraintPicker`. While set, the next `requiredRefs`
   *  taps anywhere on a construction line capture refs (anchor,
   *  curve, or whole line — most-specific wins) instead of running
   *  the normal edit / select gestures. On the final tap the picker
   *  commits a new project-level Constraint and clears itself. */
  private constraintPicker: {
    kind: ConstraintKind;
    refs: ConstraintRef[];
    /** Construction line whose Constraints section the user clicked
     *  the palette chip from. Used by the panel to render the
     *  "picking" status under the right line and by the picker to
     *  seed the first ref (the originating line is implicitly the
     *  first participant in most constraint kinds). */
    originLineId: string;
  } | null = null;

  /** Subscribers notified whenever the picker state changes (start,
   *  ref added, commit, cancel). PropertiesPanel registers one to
   *  re-render its status block as refs accumulate. */
  private constraintPickerListeners = new Set<() => void>();

  // Rotation / pivot drag state (pivotDrag / lineRotationDrag /
  // groupRotationDrag / imageRotationDrag) moved into
  // `RotationController` (#835) — accessed via `this.rotationController`.

  // Corner-resize drag state (`resizeDrag`) moved into `ResizeController`
  // (#838) — accessed via `this.resizeController`.

  override onActivate() {
    this.cancelDraft();
    // Leather reads as unfilled outlines and stitch seams show their guide
    // while the construction context is active, in any sub-mode (#649).
    this.ctx.scene.setConstructionActive(true);
    // Handles render in the Edit and Delete sub-tools. Default
    // sub-mode is Draw, so handles stay off here.
    this.ctx.scene.setShowConstructionHandles(
      isEditOrDeleteSubMode(this.subMode),
    );
    // Round-corner handles render only in the Round sub-mode (#100).
    this.ctx.scene.setShowCornerHandles(this.subMode === "round");
    // Rotation handles (issue #67) live only inside Select sub-mode —
    // they're the "move tool" affordance for spinning a single line.
    this.ctx.scene.setShowConstructionRotationHandles(
      this.subMode === "select",
    );
    // Persistent crosshair markers over every coincident-pair
    // anchor are scoped to this tool — they answer "what's already
    // snap-connected here?" and only matter while the user is in
    // Construction.
    this.ctx.scene.setConstructionCrosshairsVisible(true);
  }

  override onDeactivate() {
    this.cancelDraft();
    this.ctx.scene.setBezierToggleAffordance(null);
    this.ctx.scene.setConstructionActive(false);
    this.ctx.scene.setShowConstructionHandles(false);
    this.ctx.scene.setShowCornerHandles(false);
    this.ctx.scene.setShowConstructionRotationHandles(false);
    this.ctx.scene.setLineRotationHoverCorner(null);
    this.ctx.scene.setGroupRotationHoverCorner(null);
    this.ctx.scene.setTransientLineRotation(null);
    this.ctx.scene.setTransientGroupRotation(null);
    this.ctx.scene.setTransientPivot(null);
    this.ctx.scene.setConstructionCrosshairsVisible(false);
    this.ctx.scene.setConstructionSnapCandidateHover(null);
    this.ctx.scene.setConstructionDraftSnapPoint(null);
    this.ctx.scene.setOffsetHoverAffordance(null);
    // Scene hover already cleared above; just drop the pair (no extra
    // scene write, matching the original direct-field reset).
    this.snapController.setLiveSnap(null);
    this.editAction = "drag";
    this.vertexEditController.reset();
    this.selectEditController.reset();
    this.smoothTogglePending = null;
    this.deleteSliceController.reset();
    this.cancelConstraintPicker();
    setCanvasCursor(this.ctx.scene.getPaperScope(), "");
    setAngleHint(null);
    this.ctx.scene.clearDragAxisGuide();
  }

  /** Replay the latest Draw-mode pointer event with new modifier
   *  flags so an in-flight rect / ellipse / slot / line draft
   *  re-evaluates size-snap and angle-snap immediately when Shift
   *  is pressed or released — without having to wait for the next
   *  real mouse-move event. No-op outside Draw mode and when
   *  there's no captured cursor. */
  override onModifiersChanged(modifiers: {
    shiftKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    altKey: boolean;
  }) {
    // Slice sub-sub-tool: the angle modifier switches the live cut between
    // straight and freehand (#1013). Checked first — a slice drag is in flight,
    // so nothing below can be the intended target.
    if (this.deleteSliceController.handleModifiers(modifiers)) return;
    // Delete sub-sub-tool: a bare Shift press / release flips the
    // highlight between per-target and whole-line without a mouse
    // move (issue #670 follow-up).
    if (
      this.isEditOrDelete() &&
      this.effectiveEditAction() === "delete" &&
      this.lastEditP
    ) {
      this.refreshEditCursor(this.lastEditP, modifiers.shiftKey);
      return;
    }
    if (this.subMode !== "draw") return;
    // The Draw-mode replay (re-firing onMove / onHover with the new
    // modifier flags) lives in the composed DrawSubModeController (#850);
    // it dispatches back through this tool's full pointer handlers via
    // the DrawSubModeHost.
    this.drawController.replayModifiers(modifiers);
  }

  /** The construction equivalents of a deliberate handle drag (#40): a
   *  corner resize, a rotation / pivot drag, a vertex drag, or a line / group
   *  move. A second finger must not abandon any of them — see
   *  `ToolHandler.gestureSurvivesSecondContact`. */
  override gestureSurvivesSecondContact(): boolean {
    return (
      this.resizeController.isResizeDragActive() ||
      this.rotationController.hasActiveDrag() ||
      this.vertexEditController.hasActiveDrag() ||
      this.selectEditController.isLineDragActive() ||
      this.selectEditController.isGroupDragActive()
    );
  }

  override onCancel() {
    if (
      this.vertexEditController.hasActiveDrag() ||
      this.selectEditController.isLineDragActive() ||
      this.selectEditController.isGroupDragActive()
    ) {
      // Revert any transient move by re-rendering from the model.
      this.ctx.rerender();
      this.vertexEditController.reset();
    }
    this.rotationController.handleCancel();
    this.resizeController.handleCancel();
    // Drop every in-flight Select-mode interaction + clear the lasso
    // overlay (unconditional, matching the original `if (this.lasso)`
    // clear that sat here; the line / group nulling is a no-op when no
    // drag was active).
    this.selectEditController.reset();
    this.clearLiveSnap();
    this.smoothTogglePending = null;
    this.deleteSliceController.reset();
    this.cancelConstraintPicker();
    this.cancelDraft();
    setAngleHint(null);
  }

  // `refreshLassoOverlay` / `clearLassoOverlay` / `commitLassoSelection`
  // moved into `SelectEditController` (#848). `objectsInsideLasso` stays
  // on the tool (shared hit-testing) and is injected through the host.

  /** Every visible construction object — line AND tracing image (issues
   *  #522 / #545) — caught by the lasso, resolved in one pass. The closed
   *  lasso polygon is built once and both kinds run the same three
   *  containment checks: a line / image counts as "inside" when (a) the
   *  polygon intersects its geometry (partial overlap), (b) the polygon
   *  contains a sample point of it (full containment), or (c) it contains
   *  the polygon entirely (a tiny lasso drawn inside a big rect still
   *  selects the rect). Lines test against their rendered path; images
   *  against the Paper path of their four rotated corners.
   *
   *  SelectEditHost: injected into the composed `SelectEditController`
   *  so its lasso commit can resolve hits without owning the shared
   *  hit-testing (#848). */
  objectsInsideLasso(points: ReadonlyArray<{ x: number; y: number }>): {
    lineIds: string[];
    imageIds: string[];
  } {
    if (points.length < 3) return { lineIds: [], imageIds: [] };
    const scope = this.ctx.scene.getPaperScope();
    scope.activate();
    const polySegs = points.map((pt) => new scope.Point(pt.x, pt.y));
    const poly = new scope.Path({ segments: polySegs });
    poly.closed = true;
    poly.fillColor = null;
    poly.strokeColor = null;
    poly.data = { ephemeral: true };
    const polyFirst = polySegs[0]!;
    // The shared three-check containment test. `sample` is a point on the
    // shape (its first segment) for the full-containment check.
    const enclosed = (
      shape: paper.PathItem,
      sample: paper.Point | undefined,
    ): boolean => {
      if (poly.intersects(shape)) return true;
      if (sample && poly.contains(sample)) return true;
      return shape.contains(polyFirst);
    };
    try {
      const lineIds: string[] = [];
      for (const line of projectConstructionLines(this.ctx.history.state)) {
        const item = this.ctx.scene.getConstructionLineItem(line.id);
        if (!item) continue;
        const path = item as paper.PathItem;
        const subs: paper.Path[] =
          (path as paper.CompoundPath).children &&
          (path as paper.CompoundPath).children.length > 0
            ? ((path as paper.CompoundPath).children as paper.Path[])
            : [path as unknown as paper.Path];
        if (enclosed(path, subs[0]?.segments[0]?.point)) lineIds.push(line.id);
      }
      const imageIds: string[] = [];
      for (const image of projectTracingImages(this.ctx.history.state)) {
        if (!isVisible(image)) continue;
        const c = tracingImageCenter(image);
        const deg = tracingImageRotation(image);
        const rad = (deg * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const corner = (cx: number, cy: number) => {
          const dx = cx - c.x;
          const dy = cy - c.y;
          return new scope.Point(
            c.x + dx * cos - dy * sin,
            c.y + dx * sin + dy * cos,
          );
        };
        const rectPath = new scope.Path({
          segments: [
            corner(image.x, image.y),
            corner(image.x + image.widthMm, image.y),
            corner(image.x + image.widthMm, image.y + image.heightMm),
            corner(image.x, image.y + image.heightMm),
          ],
        });
        rectPath.closed = true;
        rectPath.data = { ephemeral: true };
        try {
          if (enclosed(rectPath, rectPath.segments[0]!.point)) {
            imageIds.push(image.id);
          }
        } finally {
          rectPath.remove();
        }
      }
      return { lineIds, imageIds };
    } finally {
      poly.remove();
    }
  }

  override onDown(s: PointerSample, p: { x: number; y: number }) {
    const scope = this.ctx.scene.getPaperScope();
    const pt = new scope.Point(p.x, p.y);

    // Constraint picker takes priority over every other gesture.
    // Each tap captures a feature ref under the pointer (anchor /
    // curve / line, most-specific wins) until the kind's required
    // count is reached, at which point the picker auto-commits.
    // Empty-space taps are ignored so a stray miss doesn't end the
    // pick. Duplicate refs (the same anchor / curve / line picked
    // twice) are ignored so the user can't satisfy a 2-ref
    // constraint by double-clicking the same target.
    if (this.constraintPicker) {
      const ref = this.resolveConstraintRef(pt);
      if (ref) {
        const picker = this.constraintPicker;
        const dupe = picker.refs.some((existing) => refsEqual(existing, ref));
        if (!dupe) {
          picker.refs.push(ref);
          const required = requiredRefsForConstraint(picker.kind);
          if (picker.refs.length >= required) {
            // commitConstraintPicker clears the state and notifies.
            this.commitConstraintPicker();
          } else {
            // Mid-pick: pin the so-far-picked refs as a yellow
            // overlay so the user sees what they've already
            // selected. Re-rendered next pick or on cancel/commit.
            this.ctx.scene.setConstructionConstraintHover({
              refs: [...picker.refs],
            });
            this.notifyConstraintPickerListeners();
          }
        }
      }
      return;
    }

    if (isOffsetSubMode(this.subMode)) {
      // Offset sub-tool (#29): clicking on ANY construction line
      // captures it as the offset source; the per-frame drag
      // updates the perpendicular distance and commits a NEW
      // derived line on release. Clicking on an existing offset
      // line creates an offset OF THAT OFFSET (a chained offset),
      // not an edit of its distance — that gesture lives in the
      // Edit sub-mode (issue #244 follow-up).
      // Single-segment offset (issue #681): the panel toggle or a held
      // Alt/Option picks just the ONE edge under the cursor instead of
      // the whole outline. Captured per-event like the Round tool's
      // modifier so the keyboard-driven toolbar flip stays decoupled.
      const wantSegment = this.singleSegmentOffset || !!s.altKey;
      const edgeHit = wantSegment ? this.hitConstructionCurve(pt) : null;
      // In single-edge mode a miss (cursor not near any edge) is a
      // no-op — never a silent whole-shape offset.
      if (wantSegment && !edgeHit) return;
      const hitId =
        edgeHit?.constructionId ?? this.ctx.scene.hitTest(pt).constructionId;
      if (hitId) {
        const proj = this.ctx.history.state;
        const source = projectConstructionLines(proj).find(
          (l) => l.id === hitId,
        );
        if (source) {
          // Issue #180: for a WHOLE-shape offset of a mirror-derived
          // line, redirect to the chain root so the offset lives on
          // the ORIGINAL geometry (the mirror sibling invariant then
          // re-creates the mirrored offset). A single-segment offset
          // instead stays on the hit line so `curveIndex` keeps
          // referring to the exact edge the user clicked.
          const sourceId =
            !edgeHit && source.mirrorSource
              ? resolveRootAllConnections(proj, source.id)
              : source.id;
          // Offset drag lives in the composed OffsetController (#837):
          // it captures the drag state + preview from the resolved
          // source / single-edge selector.
          // Drop the hover affordance now the drag owns the preview
          // (issue #148) — the offset controller draws the live dashed
          // preview + ray from here on.
          this.ctx.scene.setOffsetHoverAffordance(null);
          this.offsetController.beginCreate(
            sourceId,
            p,
            edgeHit
              ? { pathIndex: edgeHit.pathIndex, curveIndex: edgeHit.curveIndex }
              : undefined,
          );
        }
      }
      return;
    }

    if (isMirrorSubMode(this.subMode)) {
      // Mirror sub-tool (#180): the user picks an axis (a segment or
      // an anchor on any visible construction line). The current
      // construction-line selection is the set of sources to mirror.
      // A mouse commits on click, having already hovered. A finger or
      // stylus has no hover to have previewed with (#40), so for those
      // the press previews and the RELEASE commits — hence the onMove
      // and onUp routing below, which a mouse still skips entirely.
      // Delegated to the composed MirrorController (#837).
      this.mirrorController.handleDown(p, s.pointerType);
      return;
    }

    if (this.subMode === "round") {
      // Round sub-tool (#100): grab the inside-the-corner handle of a
      // construction line and drag along the bisector to set the
      // arc's radius. Delegated to the composed RoundController (#837),
      // which captures the modifier state, hit-tests the corner handle,
      // refuses derived lines, and seeds the drag + preview.
      if (this.roundController.handleDown(pt, p, s)) return;
      // No corner handle under the press (#1006). Rather than swallow the
      // click, pick the line it landed on — so you can move between lines
      // without leaving Round for the Select sub-mode and back. Handles occupy
      // a small pickup zone, so this can't shadow the rounding gesture.
      const roundPick = this.hitConstructionCurve(pt);
      if (roundPick && !this.isDerivedLineId(roundPick.constructionId)) {
        this.ctx.setSelection({
          pieceId: null,
          decorationId: null,
          constructionId: roundPick.constructionId,
        });
      }
      return;
    }

    if (this.isEditOrDelete()) {
      // Edit / Delete sub-tools: handle-grabs take priority over curve
      // hits (handles sit on top of the line). Anchor handles are
      // special in smooth-toggle mode — they become tap candidates
      // instead of vertex drags so the user can flip smooth-lock.
      const hit = this.ctx.scene.hitTest(pt);
      // Unified handle hit-test (#325 follow-up): same boundary for
      // every Edit sub-sub-tool, zoom-independent, doesn't depend on
      // paper.js's hit-test for handle dots.
      const handle = this.findVisibleHandleHit(p);
      const action = this.effectiveEditAction();
      const isSmoothToggle = action === "smooth-toggle";
      const isDelete = action === "delete";
      const isSlice = action === "slice";
      if (isSlice || isDelete) {
        // Delete / slice / prune / swipe / freehand-cut all live in
        // the composed DeleteSliceController (#836). It captures the
        // tap target + slop anchor here; onMove promotes taps to
        // drags and onUp commits.
        this.deleteSliceController.handleDown(
          p,
          pt,
          s,
          action as "slice" | "delete",
          handle,
        );
        return;
      }
      if (action === "add-point") {
        // Point-creation sub-sub-tool (#778): commit on press — there
        // is no drag gesture in this mode. A press on an existing
        // handle (anchor / bezier tip) is a no-op rather than a
        // duplicate-point insert; derived (offset / mirror) lines are
        // refused like every other direct geometry edit.
        if (!handle) {
          const curveHit = this.hitConstructionCurve(pt);
          if (curveHit && !this.isDerivedLineId(curveHit.constructionId)) {
            this.addAnchorPoint(curveHit);
            this.refreshEditCursor(p);
          }
        }
        return;
      }
      // Edit-mode drag on an OFFSET line redirects to an offset-
      // distance edit (issue #244 follow-up). Grabbing the stroke
      // (no anchor / bezier handle is rendered on derived lines)
      // captures the current distance, and the per-frame
      // perpendicular projection rewrites `offsetSource.distance`
      // on release. The "Edit refuses derived" behaviour is
      // preserved for delete / smooth-toggle / slice, which can't
      // meaningfully reshape an offset's geometry.
      if (!isSmoothToggle && !isDelete) {
        const offsetCurveHit = this.hitConstructionCurve(pt);
        if (
          offsetCurveHit &&
          this.isDerivedLineId(offsetCurveHit.constructionId)
        ) {
          const lines = projectConstructionLines(this.ctx.history.state);
          const target = lines.find(
            (l) => l.id === offsetCurveHit.constructionId,
          );
          const source = target?.offsetSource
            ? lines.find((l) => l.id === target.offsetSource!.sourceId)
            : undefined;
          if (target?.offsetSource && source) {
            // Offset-distance edit drag lives in the composed
            // OffsetController (#837): it seeds an editing drag from
            // the resolved source + the offset line's current distance.
            this.offsetController.beginEdit(
              source.id,
              target.id,
              target.offsetSource.distance,
              p,
            );
            this.ctx.setSelection({
              pieceId: null,
              decorationId: null,
              constructionId: target.id,
            });
            return;
          }
        }
      }
      // Edit-sub-mode direct-manipulation drags (#846): the bezier-tip
      // handle, anchor vertex, and curve-bend starts all live in the
      // composed VertexEditController. It captures the drag state +
      // preview (and refuses / redirects mirror-derived lines), or defers
      // an anchor press in smooth-toggle mode to the pending tap.
      if (
        this.vertexEditController.maybeStartDrag(p, pt, handle, isSmoothToggle)
      )
        return;
      // Empty space in smooth-toggle mode is a candidate tap too —
      // the on-up generous-radius lookup catches anchors the user
      // tapped near but not directly on (the iPad fingertip case
      // this whole sub-sub-tool exists to fix).
      if (isSmoothToggle) {
        this.smoothTogglePending = { x: p.x, y: p.y };
      }
      return;
    }

    if (this.subMode === "select") {
      // Rotation drag (issue #67): if the press lands on the top
      // handle of the currently-selected line, or in a corner's
      // rotation zone outside its bbox, start a rotation instead
      // of a translation. Must come BEFORE the regular hit-test
      // routing so the rotation gesture wins over the underlying
      // line / group click.
      // Pivot drag (issue #420): a press on the always-on pivot
      // crosshair picks it up to reposition the rotation centre. Runs
      // FIRST — before the corner resize handles — so that when the
      // user has parked the pivot on a bbox corner, trying to drag it
      // back off the corner grabs the pivot instead of resizing the
      // shape (the pivot and a corner handle overlap there).
      // How generously any of the handles below grab depends on what is
      // pointing (#40): a fingertip cannot find a 2 px knob, and missing it
      // does nothing at all rather than something wrong.
      this.rotationController.setPointerKind(s.pointerType);
      this.resizeController.setPointerKind(s.pointerType);
      if (this.rotationController.maybeStartPivotDrag(p)) return;
      // Corner resize (issue #388) claims the press before rotation /
      // translation when it lands on a resize handle of the selected
      // line — the handle sits AT the bbox corner, inside the rotation
      // ring just beyond it.
      if (this.resizeController.maybeStartDrag(p)) return;
      if (this.rotationController.maybeStartGroupRotationDrag(p, s.shiftKey))
        return;
      if (this.rotationController.maybeStartLineRotationDrag(p, s.shiftKey))
        return;
      if (this.rotationController.maybeStartImageRotationDrag(p, s.shiftKey))
        return;
      // Select sub-tool pick / move-drag / lasso resolution (#848):
      // the whole-object move drags (single line / group / bbox /
      // ad-hoc multi), the deferred Alt-drag clone arming (#345), and
      // the marquee/lasso gesture all live in the composed
      // SelectEditController. It resolves the hit into a selection
      // change and starts the appropriate interaction (calling back
      // through the host for the shared hit-test / selection / snap /
      // drill machinery); the rotation / resize / pivot guards above
      // stay on the tool and run first, so the exact dispatch order is
      // preserved.
      this.selectEditController.maybeStartDrag(s, p, pt);
      return;
    }

    // Draw sub-mode (default) — the existing drawing flow, extracted in
    // full into the composed DrawSubModeController (#850). It seeds the
    // drag-out / poly draft (`startPt` / `currentPt` / `buffer`) and
    // drives the shared DrawGestureEngine for the polyline / polygon
    // click-to-add gesture, calling back through the DrawSubModeHost for
    // the shared snap tiers + commit.
    this.drawController.onDown(s, p);
  }

  override onMove(s: PointerSample, p: { x: number; y: number }) {
    // Mirror by drag, for a pointer that cannot hover (#40): the preview
    // follows the finger so the axis is visible before it is chosen.
    if (
      isMirrorSubMode(this.subMode) &&
      this.mirrorController.handleMove(p, s.pointerType)
    )
      return;
    if (this.resizeController.handleMove(p, s)) return;
    // Rotation / pivot drags (#835): the controller dispatches the
    // active one in the original order (pivot → group → image → line).
    if (this.rotationController.handleMove(p, s)) return;
    if (this.subMode === "draw") {
      // Capture for `onModifiersChanged` so a mid-drag Shift press
      // can replay the preview with the new modifier state — only
      // the Draw sub-mode has modifier-sensitive previews
      // (size-snap on rect / ellipse, angle-snap on slot / line).
      // The capture state lives on the DrawSubModeController (#850).
      this.drawController.rememberSample(s, p);
    }
    // Offset / round drag advancement lives in their composed
    // controllers (#837): the offset preview tracks the signed
    // perpendicular distance; the round preview tracks the radius the
    // cursor implies (capturing the Ctrl/Cmd modifier per-frame).
    if (this.offsetController.handleMove(p)) return;
    if (this.roundController.handleMove(p, s)) return;
    if (this.smoothTogglePending) {
      // If the cursor moves more than ~3 mm we treat the gesture as
      // a drag (which the smooth-toggle mode doesn't act on) and
      // drop the pending tap so `onUp` doesn't fire a spurious
      // toggle on what was clearly an aborted gesture.
      const dx = p.x - this.smoothTogglePending.x;
      const dy = p.y - this.smoothTogglePending.y;
      if (Math.hypot(dx, dy) > 3) this.smoothTogglePending = null;
      return;
    }
    // Delete / slice drag advancement lives in the composed
    // DeleteSliceController (#836): it advances the swipe / freehand
    // strokes, promotes taps to drags past the slop, and drops
    // pending taps that wandered.
    if (this.deleteSliceController.handleMove(p, s)) return;
    // Edit-sub-mode direct-manipulation drags (#846): the vertex /
    // bezier-handle / curve-bend apply passes live in the composed
    // VertexEditController, dispatched in the original order
    // (vertex → bezier handle → curve).
    if (this.vertexEditController.handleMove(s, p)) return;
    // Select-sub-mode move drags + lasso extend (#848): the lasso
    // overlay tracking, the deferred Alt-drag clone arming (#345), and
    // the whole-object move-drag apply passes live in the composed
    // SelectEditController, dispatched in the original order (lasso →
    // alt-duplicate-pending → group → line).
    if (this.selectEditController.handleMove(p, s)) return;
    if (this.subMode !== "draw") return;
    // Draw sub-mode move — the drag-out preview advancement and the
    // polyline / polygon pen-drag both live in the composed
    // DrawSubModeController (#850).
    this.drawController.onMove(s, p);
  }

  /** Pick the right cursor for the current sub-mode at point `p`.
   *  Used by `onHover` for the live tracking and by `onUp` after a
   *  curve-bend drag completes so the cursor doesn't stay stuck at
   *  "grabbing" until the user moves the pointer. */
  refreshEditCursor(p: { x: number; y: number }, shiftKey = false) {
    const scope = this.ctx.scene.getPaperScope();
    const pt = new scope.Point(p.x, p.y);
    const hit = this.ctx.scene.hitTest(pt);
    // The smooth/sharp glyph belongs to exactly one hover state (#147), so
    // clear it up front and let that state put it back. This method has a
    // dozen early returns; a per-branch clear is how a cursor affordance ends
    // up stuck on screen.
    this.ctx.scene.setBezierToggleAffordance(null);
    // While a constraint pick is open, every other cursor decision
    // is short-circuited — the only thing the user can do is tap a
    // construction-line feature. "crosshair" over candidates,
    // "default" over empty space, so it's clear what counts. Compose
    // the canvas overlay too: pinned picked refs + the feature under
    // the cursor (if any and not already picked), so the user gets a
    // live preview of what their click is about to add.
    if (this.constraintPicker) {
      const ref = this.resolveConstraintRef(pt);
      setCanvasCursor(scope, ref ? "crosshair" : "default");
      const picked = this.constraintPicker.refs;
      const hoverIsNew =
        ref !== null && !picked.some((existing) => refsEqual(existing, ref));
      const refs = hoverIsNew ? [...picked, ref] : [...picked];
      if (refs.length > 0) {
        this.ctx.scene.setConstructionConstraintHover({ refs });
      } else {
        this.ctx.scene.setConstructionConstraintHover(null);
      }
      return;
    }
    if (this.isEditOrDelete()) {
      // Every Edit / Delete sub-tool uses the SAME handle hit-test —
      // `findVisibleHandleHit` returns the anchor or bezier tip
      // whose visible circle the cursor is inside (anchor wins
      // overlap; selection wins ties). Per-tool branches only pick
      // the cursor glyph and overlay; they no longer differ on
      // WHAT counts as a hit.
      const handle = this.findVisibleHandleHit(p);
      // Remember the hover point so a bare Shift press / release can
      // re-evaluate the Delete highlight (issue #670 follow-up).
      this.lastEditP = p;
      if (this.effectiveEditAction() === "delete") {
        // Holding Shift forces a WHOLE-LINE delete regardless of what
        // the cursor's over — anchor, handle, segment or stub all read
        // as "remove the entire line" (issue #670 follow-up). Mirror-
        // derived lines stay refused; offsets already delete whole.
        if (shiftKey) {
          const lineHit = this.lineUnderCursorForDelete(p, pt, handle);
          if (
            lineHit &&
            !this.isMirrorOnlyDerivedLineId(lineHit.constructionId)
          ) {
            this.ctx.scene.setConstructionDeleteHover({
              kind: "line",
              constructionId: lineHit.constructionId,
              pathIndex: lineHit.pathIndex,
            });
            setCanvasCursor(scope, "not-allowed");
          } else {
            this.ctx.scene.setConstructionDeleteHover(null);
            setCanvasCursor(scope, lineHit ? "not-allowed" : "default");
          }
          return;
        }
        // Delete sub-sub-tool: any of (handle dot / anchor dot /
        // curve stroke) reads as a delete target. Surface the red
        // overlay on whichever target the cursor's over so the
        // user can see exactly what would get deleted on tap. The
        // "not-allowed" glyph reinforces the "this gesture removes
        // something" intent.
        //
        // Offset-derived lines light up just the STRETCH the tap would
        // switch off (issue #116) — the run generated by the source
        // edge under the cursor. Offsets with no addressable stretch
        // (single-edge ones) keep issue #327's whole-line red, which is
        // still what tapping them does.
        if (handle?.kind === "bezier-tip") {
          if (this.isOffsetDerivedLineId(handle.constructionId)) {
            this.setOffsetDeleteHover(
              handle.constructionId,
              p,
              handle.pathIndex,
            );
          } else {
            this.ctx.scene.setConstructionDeleteHover({
              kind: "handle",
              constructionId: handle.constructionId,
              pathIndex: handle.pathIndex,
              segmentIndex: handle.segmentIndex,
              side: handle.side,
            });
          }
          setCanvasCursor(scope, "not-allowed");
          return;
        }
        if (handle?.kind === "anchor") {
          if (this.isOffsetDerivedLineId(handle.constructionId)) {
            this.setOffsetDeleteHover(
              handle.constructionId,
              p,
              handle.pathIndex,
            );
          } else {
            this.ctx.scene.setConstructionDeleteHover({
              kind: "anchor",
              constructionId: handle.constructionId,
              pathIndex: handle.pathIndex,
              segmentIndex: handle.segmentIndex,
            });
          }
          setCanvasCursor(scope, "not-allowed");
          return;
        }
        const curveHit = this.hitConstructionCurve(pt);
        if (curveHit) {
          if (this.isOffsetDerivedLineId(curveHit.constructionId)) {
            this.setOffsetDeleteHover(
              curveHit.constructionId,
              p,
              curveHit.pathIndex,
            );
          } else {
            // Hanging-end prune (issue #670): when the cursor's on a stub
            // that pokes past a crossing, highlight just that stub; else
            // fall back to the per-segment highlight.
            const prune = this.findPruneTarget(pt, curveHit);
            if (prune) {
              this.ctx.scene.setConstructionDeleteHover({
                kind: "prune",
                constructionId: prune.constructionId,
                pathData: prune.overlayPathData,
              });
            } else {
              this.ctx.scene.setConstructionDeleteHover({
                kind: "segment",
                constructionId: curveHit.constructionId,
                pathIndex: curveHit.pathIndex,
                curveIndex: curveHit.curveIndex,
              });
            }
          }
          setCanvasCursor(scope, "not-allowed");
          return;
        }
        this.ctx.scene.setConstructionDeleteHover(null);
        setCanvasCursor(scope, "default");
        return;
      }
      if (this.editAction === "slice") {
        // Slice sub-sub-tool: the cursor is always actionable here
        // — a tap on a curve does a single-point slice, a drag
        // anywhere on the canvas draws a freehand cut. Crosshair
        // everywhere reflects "click or drag to cut" uniformly.
        setCanvasCursor(scope, "crosshair");
        return;
      }
      if (this.editAction === "add-point") {
        // Point-creation (#778): the "copy" cursor (pointer + little
        // plus) over an insertable stretch of curve reads as "click
        // to add a point here". Existing handles and derived (offset
        // / mirror) lines aren't insertable — handles read as
        // "default" (nothing to add), derived curves as
        // "not-allowed" (their geometry is owned by the source).
        if (handle) {
          setCanvasCursor(scope, "default");
        } else {
          const curveHit = this.hitConstructionCurve(pt);
          if (curveHit && !this.isDerivedLineId(curveHit.constructionId)) {
            setCanvasCursor(scope, "copy");
          } else if (curveHit) {
            setCanvasCursor(scope, "not-allowed");
          } else {
            setCanvasCursor(scope, "default");
          }
        }
        return;
      }
      if (this.editAction === "smooth-toggle") {
        // Tangent endpoints + curve strokes are still draggable in
        // this mode (only anchor node movement is disabled), so
        // surface their drag affordances. Anchor pickup reads as
        // "pointer" (tap to toggle) plus the smooth/sharp glyph beside the
        // cursor (#147) — the pointer says "clickable", the glyph says what
        // the click does. Identical to the leather Edit-points hover, because
        // it is the same gesture on the same kind of point.
        if (handle?.kind === "anchor") {
          setCanvasCursor(scope, "pointer");
          this.ctx.scene.setBezierToggleAffordance(p);
          return;
        }
        if (handle?.kind === "bezier-tip") {
          setCanvasCursor(scope, "move");
        } else if (this.hitConstructionCurve(pt)) {
          setCanvasCursor(scope, "grab");
        } else {
          setCanvasCursor(scope, "default");
        }
        return;
      }
      // Drag-point (P): anchor + tangent endpoint both read as
      // "move" (drag the dot to reshape); curve hits read as "grab"
      // (pull the line into an arc); empty space is default.
      if (handle) {
        setCanvasCursor(scope, "move");
      } else if (this.hitConstructionCurve(pt)) {
        setCanvasCursor(scope, "grab");
      } else {
        setCanvasCursor(scope, "default");
      }
    } else if (this.subMode === "select") {
      // Anything draggable in select mode (a selected-group bbox frame
      // or any construction line body) reads as `move` — the 4-way
      // arrows cursor is the universal "drag in any direction" symbol,
      // signalling a translate before the user commits to the drag.
      // The `grab` hand is reserved for rotation gestures (the top
      // handle + rotation-around-the-point anchor) so the two affordances
      // stay visually distinct (issue #397).
      const draggable = hit.constructionGroupId || hit.constructionId;
      setCanvasCursor(scope, draggable ? "move" : "default");
    } else if (isOffsetSubMode(this.subMode)) {
      // Offset sub-mode (issue #148): a plain pointer, NOT a crosshair —
      // the offset gesture starts by grabbing an existing line, so the
      // pointer reads "point at a line", not "place a point". When the
      // cursor is over an offsettable line, a small perpendicular arrow
      // (with an offset-line tick at its tip) previews the offset that a
      // drag from here would create; the arrow points to the side the
      // offset will land on.
      setCanvasCursor(scope, "default");
      this.ctx.scene.setOffsetHoverAffordance(this.computeOffsetHoverSpec(pt));
    } else {
      setCanvasCursor(scope, "crosshair");
    }
  }

  /** Geometry for the Offset sub-mode's hover affordance (issue #148).
   *  Returns the cursor anchor plus resolved unit vectors — `dir`
   *  perpendicular to the hovered line (the offset direction) and `tan`
   *  parallel to it (the offset-line tick) — or `null` when the cursor
   *  isn't over an offsettable construction curve. */
  private computeOffsetHoverSpec(pt: paper.Point): {
    x: number;
    y: number;
    dirX: number;
    dirY: number;
    tanX: number;
    tanY: number;
  } | null {
    const hit = this.hitConstructionCurve(pt);
    if (!hit) return null;
    const scope = this.ctx.scene.getPaperScope();
    const loc = hit.curve.getLocationAtTime(hit.t);
    if (!loc || !loc.tangent || loc.tangent.length < 1e-9) return null;
    const foot = loc.point;
    const tan = loc.tangent.normalize();
    // Left-of-direction normal in y-down space — the offset sign
    // convention's default side (mirrors `refreshOffsetPreview`).
    const leftNormal = new scope.Point(tan.y, -tan.x);
    // Prefer the side the cursor is actually on when it's clearly off
    // the line; when it sits essentially ON the line (the usual hover
    // case, since the pointer hotspot lands on the stroke) fall back to
    // a deterministic default: outward for a closed source, else the
    // left normal — so the arrow never jitters as the cursor grazes the
    // line.
    const delta = pt.subtract(foot);
    const along = delta.dot(tan);
    const perp = delta.subtract(tan.multiply(along));
    const deadZone = 1.5 / (scope.view?.zoom ?? 1);
    const normal =
      perp.length > deadZone
        ? perp.normalize()
        : (this.outwardNormalForSource(hit.constructionId, foot, leftNormal) ??
          leftNormal);
    return {
      x: pt.x,
      y: pt.y,
      dirX: normal.x,
      dirY: normal.y,
      tanX: tan.x,
      tanY: tan.y,
    };
  }

  /** For a CLOSED offset source, the normal pointing OUT of the shape at
   *  `foot` (so the affordance arrow reads "offset outward" by default).
   *  Returns `null` for open paths or on any parse failure, letting the
   *  caller fall back to the left-of-direction normal. */
  private outwardNormalForSource(
    constructionId: string,
    foot: paper.Point,
    leftNormal: paper.Point,
  ): paper.Point | null {
    const line = projectConstructionLines(this.ctx.history.state).find(
      (l) => l.id === constructionId,
    );
    if (!line) return null;
    const scope = this.ctx.scene.getPaperScope();
    let cp: paper.CompoundPath | null = null;
    try {
      cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
      const subs: paper.Path[] =
        cp.children && cp.children.length > 0
          ? (cp.children as paper.Path[])
          : [cp as unknown as paper.Path];
      const closed = subs.length > 0 && subs.every((s) => s.closed);
      if (!closed) return null;
      // Probe just off the line along the left normal: if that point is
      // inside the shape, "out" is the opposite direction.
      const eps = 0.05;
      const inside = cp.contains(foot.add(leftNormal.multiply(eps)));
      return inside ? leftNormal.multiply(-1) : leftNormal;
    } catch {
      return null;
    } finally {
      cp?.remove();
    }
  }

  override onHover(s: PointerSample, p: { x: number; y: number }) {
    this.refreshEditCursor(p, !!s.shiftKey);
    if (this.subMode === "select") {
      // Rotation / pivot hover (#835): the controller runs the pivot
      // crosshair, group, per-line and per-image handle hovers in the
      // original priority order. It returns true when the pivot
      // crosshair claimed the cursor, in which case we stop here.
      if (this.rotationController.handleHover(p)) return;
    }
    if (isMirrorSubMode(this.subMode)) {
      // Mirror axis-candidate hover + preview lives in the composed
      // MirrorController (#837).
      this.mirrorController.handleHover(p);
      return;
    }
    if (this.subMode !== "draw") return;
    // Draw sub-mode hover — the modifier-replay capture, the poly
    // rubber-band hover, and the drag-out snap-ring preview live in the
    // composed DrawSubModeController (#850).
    this.drawController.onHover(s, p);
  }

  /** Construction-line ids the user has actively selected, expanded
   *  to include every descendant line of any selected group. The
   *  distance-based hit-test helpers below (anchor, curve, corner)
   *  use this to bias an otherwise-tied pick toward a selected
   *  line's handle (issue #262). Public — the composed
   *  `RoundController` (#837) reads it through the `RoundHost` hook to
   *  bias corner-handle ties toward selected lines. */
  selectedConstructionLineIds(): Set<string> {
    const sel = this.ctx.scene.getSelection();
    const ids = new Set<string>();
    if (sel.constructionIds && sel.constructionIds.length > 0) {
      for (const id of sel.constructionIds) ids.add(id);
    } else if (sel.constructionId) {
      ids.add(sel.constructionId);
    }
    const proj = this.ctx.history.state;
    for (const gid of sel.constructionGroupIds ?? []) {
      for (const lid of descendantConstructionLineIds(proj, gid)) {
        ids.add(lid);
      }
    }
    return ids;
  }

  /** UNIFIED handle hit-test for every Construction Edit sub-sub-tool
   *  (P drag-point, B smooth-toggle, X delete, Slice).
   *
   *  Scans every visible construction-line segment in project-mm
   *  space and returns the nearest anchor (1.1 + 1/zoom mm) or
   *  bezier tip (0.7 + 0.25/zoom mm) the cursor is inside the
   *  visible circle of. The "+ ?/zoom" term is the visible stroke
   *  half-width — the dot's stroke renders at fixed CSS pixels
   *  (`strokeScaling = false`), so its project-mm width shrinks at
   *  high zoom; including it keeps the boundary exactly aligned
   *  with the OUTER edge of the visible dot at every zoom level.
   *
   *  Tip OVER anchor when both within range (issue #329): a short
   *  tangent handle whose tip dot sits inside the anchor's visible
   *  circle would otherwise be unreachable — the anchor would steal
   *  every click and the user could never grab the tip to lengthen
   *  the handle. The matching z-order (tip dots are rendered AFTER
   *  the anchor dot in the scene group) keeps the tip visible too.
   *
   *  Selection bias (issue #262): a handle on a selected line beats
   *  every handle on an unselected line within range; nearest wins
   *  among ties.
   *
   *  Why this replaces both `Scene.hitTest`'s construction-handle
   *  branch AND the old `hitConstructionAnchor` 12 px fingertip
   *  pickup: paper.js's hit-test on `opacity: 0` hit-pads and
   *  transparent fills proved unreliable at high zoom (only the
   *  filled centre dot registered), and `12 / zoom` ("12 mm" but
   *  really 12 px) shrank below the visible dot at high zoom too.
   *  A direct distance check in project-mm is zoom-independent and
   *  doesn't depend on any paper hit-test quirks. */
  protected findVisibleHandleHit(pt: { x: number; y: number }):
    | {
        kind: "anchor";
        constructionId: string;
        pathIndex: number;
        segmentIndex: number;
      }
    | {
        kind: "bezier-tip";
        constructionId: string;
        pathIndex: number;
        segmentIndex: number;
        side: "in" | "out";
      }
    | null {
    const project = this.ctx.history.state;
    const scope = this.ctx.scene.getPaperScope();
    const zoom = scope.view?.zoom ?? 1;
    // Visible dot radius + the rendered stroke's project-mm half-
    // width. Anchor stroke is 2 px → half = 1 px = 1/zoom mm.
    // Bezier tip stroke is 0.5 px → half = 0.25 px = 0.25/zoom mm.
    // A finger grabs them wider (#1150); the dots are drawn the same size.
    // Tips get no screen floor: they win over the anchor within range, so a
    // floor would let a short handle's tip steal the anchor's presses.
    const anchorR = dotHitRadiusMm(1.1 + 1 / zoom, zoom, currentPointerKind());
    const tipR = hitRadiusFor(0.7 + 0.25 / zoom, currentPointerKind());
    const preferIds = this.selectedConstructionLineIds();
    type AnchorCand = {
      kind: "anchor";
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
      distance: number;
      selected: boolean;
    };
    type TipCand = {
      kind: "bezier-tip";
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
      side: "in" | "out";
      distance: number;
      selected: boolean;
    };
    let bestAnchor: AnchorCand | null = null;
    let bestAnchorSelected: AnchorCand | null = null;
    let bestTip: TipCand | null = null;
    let bestTipSelected: TipCand | null = null;
    for (const line of projectConstructionLines(project)) {
      if (!isVisible(line)) continue;
      let path: paper.CompoundPath;
      try {
        path = new scope.CompoundPath({
          pathData: line.pathData,
          insert: false,
        });
      } catch {
        continue;
      }
      const subpaths: paper.Path[] =
        path.children && path.children.length > 0
          ? (path.children as paper.Path[])
          : [path as unknown as paper.Path];
      const selected = preferIds.has(line.id);
      for (let pathIndex = 0; pathIndex < subpaths.length; pathIndex++) {
        const sub = subpaths[pathIndex]!;
        if (!sub.segments) continue;
        for (
          let segmentIndex = 0;
          segmentIndex < sub.segments.length;
          segmentIndex++
        ) {
          const seg = sub.segments[segmentIndex]!;
          // Bezier tips first — but we only return one if no anchor
          // matches, so even if a tip is closer in distance the
          // anchor still wins when overlapping.
          for (const side of ["in", "out"] as const) {
            const h = side === "in" ? seg.handleIn : seg.handleOut;
            if (h.isZero()) continue;
            const tx = seg.point.x + h.x;
            const ty = seg.point.y + h.y;
            const td = Math.hypot(tx - pt.x, ty - pt.y);
            if (td > tipR) continue;
            const c: TipCand = {
              kind: "bezier-tip",
              constructionId: line.id,
              pathIndex,
              segmentIndex,
              side,
              distance: td,
              selected,
            };
            if (!bestTip || td < bestTip.distance) bestTip = c;
            if (selected && (!bestTipSelected || td < bestTipSelected.distance))
              bestTipSelected = c;
          }
          const ad = Math.hypot(seg.point.x - pt.x, seg.point.y - pt.y);
          if (ad > anchorR) continue;
          const c: AnchorCand = {
            kind: "anchor",
            constructionId: line.id,
            pathIndex,
            segmentIndex,
            distance: ad,
            selected,
          };
          if (!bestAnchor || ad < bestAnchor.distance) bestAnchor = c;
          if (
            selected &&
            (!bestAnchorSelected || ad < bestAnchorSelected.distance)
          )
            bestAnchorSelected = c;
        }
      }
      path.remove();
    }
    const tip = bestTipSelected ?? bestTip;
    if (tip) {
      return {
        kind: "bezier-tip",
        constructionId: tip.constructionId,
        pathIndex: tip.pathIndex,
        segmentIndex: tip.segmentIndex,
        side: tip.side,
      };
    }
    const anchor = bestAnchorSelected ?? bestAnchor;
    if (anchor) {
      return {
        kind: "anchor",
        constructionId: anchor.constructionId,
        pathIndex: anchor.pathIndex,
        segmentIndex: anchor.segmentIndex,
      };
    }
    return null;
  }

  /** Find the nearest construction-line curve to the cursor and
   *  return it (with its bezier `t`) when it's within grab range.
   *  Uses `getNearestLocation` rather than `hitTest({stroke})` because
   *  the construction stroke is dashed — paper's stroke hit-test
   *  reports nothing when the cursor lands in a dash gap, which made
   *  the curve feel un-grabbable. Distance-from-curve is gap-agnostic
   *  and matches the user's intent (anywhere along the visual line is
   *  the same line). */
  /** DeleteSliceHost hook — also used by the tool's own edit hover. */
  hitConstructionCurve(pt: paper.Point): {
    constructionId: string;
    pathIndex: number;
    curveIndex: number;
    curve: paper.Curve;
    t: number;
  } | null {
    const project = this.ctx.history.state;
    // Tolerance scales with zoom so the grab feel is the same at every
    // zoom level — narrow lines stay grabbable when zoomed out.
    const scope = this.ctx.scene.getPaperScope();
    const tolerance =
      hitRadiusFor(6, currentPointerKind()) / (scope.view?.zoom ?? 1);
    type Candidate = {
      constructionId: string;
      pathIndex: number;
      curveIndex: number;
      curve: paper.Curve;
      t: number;
      distance: number;
    };
    const preferIds = this.selectedConstructionLineIds();
    let bestAny: Candidate | null = null;
    let bestSelected: Candidate | null = null;
    for (const line of projectConstructionLines(project)) {
      if (!isVisible(line)) continue;
      const item = this.ctx.scene.getConstructionLineItem(line.id);
      if (!item) continue;
      const compound = item as paper.CompoundPath;
      const subpaths: paper.Path[] =
        compound.children && compound.children.length > 0
          ? (compound.children as paper.Path[])
          : [item as unknown as paper.Path];
      const isSelected = preferIds.has(line.id);
      for (let pathIndex = 0; pathIndex < subpaths.length; pathIndex++) {
        const sub = subpaths[pathIndex]!;
        const loc = sub.getNearestLocation(pt);
        if (!loc || !loc.curve) continue;
        const distance = loc.point.getDistance(pt);
        if (distance > tolerance) continue;
        const candidate: Candidate = {
          constructionId: line.id,
          pathIndex,
          curveIndex: loc.curve.index,
          curve: loc.curve,
          t: loc.time,
          distance,
        };
        if (!bestAny || distance < bestAny.distance) bestAny = candidate;
        if (isSelected && (!bestSelected || distance < bestSelected.distance)) {
          bestSelected = candidate;
        }
      }
    }
    // Selected-line hits beat unselected ones within the same
    // tolerance (issue #262), even if an unselected curve is
    // marginally closer.
    const best = bestSelected ?? bestAny;
    return best
      ? {
          constructionId: best.constructionId,
          pathIndex: best.pathIndex,
          curveIndex: best.curveIndex,
          curve: best.curve,
          t: best.t,
        }
      : null;
  }

  /** Paint the Delete highlight for an offset line: the single stretch
   *  the tap would switch off (issue #116), resolved by the same
   *  `offsetSegmentPickAt` the tap itself uses so the red run and the
   *  edge that actually goes away can't disagree. Falls back to the
   *  whole-line red when no stretch is addressable — a single-edge
   *  offset, or a source that has gone — which is also exactly when the
   *  tap still deletes the whole line.
   *
   *  The pick samples the derived path, so the result is memoised per
   *  (line, geometry, edge): hover moves along one stretch reuse it
   *  instead of re-walking the path on every pointer event. */
  private offsetDeleteHoverCache: {
    key: string;
    pathData: string;
  } | null = null;

  private setOffsetDeleteHover(
    constructionId: string,
    p: { x: number; y: number },
    pathIndex: number,
  ): void {
    const project = this.ctx.history.state;
    const line = projectConstructionLines(project).find(
      (l) => l.id === constructionId,
    );
    const segment = line
      ? offsetSourceSegmentAt(project, constructionId, p, this.getScope())
      : null;
    // The cache is keyed on the geometry too, so a source edit that
    // re-derives the offset invalidates it even though the edge address
    // is unchanged.
    const key = segment
      ? `${constructionId}|${line!.pathData}|${segment.pathIndex}|${segment.curveIndex}`
      : null;
    let runPathData: string | null = null;
    if (key !== null && this.offsetDeleteHoverCache?.key === key) {
      runPathData = this.offsetDeleteHoverCache.pathData;
    } else if (key !== null && segment) {
      runPathData = offsetSegmentRun(
        project,
        constructionId,
        segment,
        this.getScope(),
      );
      this.offsetDeleteHoverCache = runPathData
        ? { key, pathData: runPathData }
        : null;
    }
    if (!runPathData) {
      this.ctx.scene.setConstructionDeleteHover({
        kind: "line",
        constructionId,
        pathIndex,
      });
      return;
    }
    this.ctx.scene.setConstructionDeleteHover({
      kind: "offset-segment",
      constructionId,
      pathData: runPathData,
    });
  }

  /** Delete sub-sub-tool: decide whether the cursor (over construction
   *  curve `curveHit`) is sitting on a *hanging end* — a stub of the line
   *  that pokes past where another visible line crosses it (issue #670).
   *  When it is, the Delete gesture prunes the line back to that crossing
   *  instead of removing the whole segment. Returns the crossing point to
   *  cut at, which half to keep, and the overhanging portion's pathData
   *  for the red highlight; `null` falls back to today's per-segment
   *  behaviour (no crossing, or the cursor's on an interior stretch).
   *
   *  Closed and derived (offset / mirror) lines are excluded: a loop has
   *  no end to hang, and derived geometry already routes to whole-line
   *  delete. */
  /** DeleteSliceHost hook — hanging-end prune target under the cursor. */
  findPruneTarget(
    pt: paper.Point,
    curveHit: { constructionId: string; pathIndex: number },
  ): {
    constructionId: string;
    pathIndex: number;
    cutPoint: { x: number; y: number };
    keepSide: "left" | "right";
    overlayPathData: string;
  } | null {
    if (this.isDerivedLineId(curveHit.constructionId)) return null;
    const project = this.ctx.history.state;
    const item = this.ctx.scene.getConstructionLineItem(
      curveHit.constructionId,
    );
    if (!item) return null;
    const compound = item as paper.CompoundPath;
    const subpaths: paper.Path[] =
      compound.children && compound.children.length > 0
        ? (compound.children as paper.Path[])
        : [item as unknown as paper.Path];
    const sub = subpaths[curveHit.pathIndex];
    if (!sub || sub.closed) return null;
    const length = sub.length;
    if (!(length > 0)) return null;

    const cursorLoc = sub.getNearestLocation(pt);
    if (!cursorLoc) return null;

    // Offsets along `sub` where any OTHER visible line crosses it.
    const crossings: number[] = [];
    for (const other of projectConstructionLines(project)) {
      if (other.id === curveHit.constructionId) continue;
      if (!isVisible(other)) continue;
      const otherItem = this.ctx.scene.getConstructionLineItem(other.id);
      if (!otherItem) continue;
      let inters: paper.CurveLocation[] = [];
      try {
        inters = sub.getIntersections(otherItem as paper.PathItem);
      } catch {
        continue;
      }
      for (const it of inters) crossings.push(it.offset);
    }

    const hang = findHangingEnd(cursorLoc.offset, crossings, length);
    if (!hang) return null;

    const cutLoc = sub.getLocationAt(hang.cutOffset);
    if (!cutLoc) return null;
    const cutPoint = { x: cutLoc.point.x, y: cutLoc.point.y };

    // Carve the overhanging portion [from, to] out of a throwaway clone so
    // the highlight traces exactly the stub that's about to be pruned.
    let overlayPathData: string;
    const clone = sub.clone({ insert: false }) as paper.Path;
    try {
      const splitLoc = clone.getLocationAt(hang.cutOffset);
      const right = splitLoc ? (clone as paper.Path).splitAt(splitLoc) : null;
      if (hang.end === "start") {
        // Stub is [0, cut] → the left half is the clone itself.
        overlayPathData = clone.pathData;
        if (right && right !== clone) right.remove();
      } else {
        overlayPathData = right ? right.pathData : clone.pathData;
        if (right && right !== clone) right.remove();
      }
    } catch {
      clone.remove();
      return null;
    }
    clone.remove();
    if (!overlayPathData) return null;

    return {
      constructionId: curveHit.constructionId,
      pathIndex: curveHit.pathIndex,
      cutPoint,
      keepSide: hang.end === "start" ? "right" : "left",
      overlayPathData,
    };
  }

  override onUp(s: PointerSample, p: { x: number; y: number }) {
    // #746 / #747: any in-flight gesture ray clears on release; the committing
    // branches below don't need to each remember to do it.
    this.ctx.scene.clearGuideRays();
    // Mirror commits on RELEASE for a pointer that cannot hover (#40) — the
    // press previewed, the drag adjusted, lifting decides.
    if (
      isMirrorSubMode(this.subMode) &&
      this.mirrorController.handleUp(p, s.pointerType)
    )
      return;
    if (this.resizeController.handleUp(p, s)) return;
    if (this.rotationController.handlePivotUp(p, s)) return;
    // Option/Alt-press that never moved past the slop (#345): the
    // duplicate was deferred and never armed, so there's nothing to
    // clone — drop the pending state and treat the gesture as a no-op
    // click rather than stranding a copy on the original. Owned by the
    // SelectEditController (#848); dispatched here so the exact early
    // position (before the pen-tool / rotation / offset releases) is
    // preserved.
    if (this.selectEditController.handleAltDuplicateUp()) return;
    // Pen-tool gesture (#217): release ends the bezier-drag for the
    // most-recently-placed polyline vertex. The handles set during
    // the drag stay on the vertex; the next mouseDown places the
    // following anchor. Engine owned by the DrawSubModeController (#850).
    this.drawController.clearBezierDrag();
    // Line / group / image rotation commit (#835): dispatched by the
    // controller in the original order (group → image → line).
    if (this.rotationController.handleRotationUp(p, s)) return;
    // Round modifier capture (#837): the Ctrl/Cmd state is snapshotted
    // BEFORE the offset case, preserving the original dispatch order.
    // The two drags are mutually exclusive, so this only ever fires
    // when a round drag is live.
    this.roundController.captureShiftModifier(s);
    // Offset / round release lives in their composed controllers
    // (#837): offset commits a new line or a distance edit; round
    // commits (or un-rounds, or applies a preset on a click) the
    // corner fillet.
    if (this.offsetController.handleUp()) return;
    if (this.roundController.handleUp()) return;
    // Lasso commit (#848): owned by the SelectEditController, dispatched
    // here so it runs after the offset / round releases and before the
    // smooth-toggle / delete / vertex-edit releases — original order.
    if (this.selectEditController.handleLassoUp()) return;
    if (this.smoothTogglePending) {
      // Pending tap survived the slop window — flip the anchor's
      // smooth-lock if the tap is still inside an anchor's visible
      // circle. No-op if the tap landed in empty space or on a
      // bezier tip (tips can't be smooth-toggled).
      const tap = this.smoothTogglePending;
      this.smoothTogglePending = null;
      const hit = this.findVisibleHandleHit(tap);
      if (hit && hit.kind === "anchor") {
        this.toggleSmoothLock(
          hit.constructionId,
          hit.pathIndex,
          hit.segmentIndex,
        );
      }
      this.refreshEditCursor(p);
      return;
    }
    // Delete / slice release lives in the composed DeleteSliceController
    // (#836): it commits the swipe / freehand / tap gesture and clears
    // its state. A consumed release refreshes the edit cursor (matching
    // the original per-branch behaviour); a bare slop-anchor clear
    // returns false and falls through to the rest of the dispatch.
    if (this.deleteSliceController.handleUp(p)) {
      this.refreshEditCursor(p);
      return;
    }
    // Edit-sub-mode direct-manipulation drags (#846): the vertex /
    // bezier-handle / curve-bend commits live in the composed
    // VertexEditController. It dispatches the three (mutually exclusive)
    // states in the original order and refreshes the edit cursor for the
    // handle / curve releases.
    if (this.vertexEditController.handleUp(p)) return;
    // Select-sub-mode move-drag commits (#848): the group-drag and
    // line-drag releases live in the composed SelectEditController,
    // dispatched in the original order (group → line); the two states
    // are mutually exclusive (a single onDown seeds exactly one).
    if (this.selectEditController.handleUp(p, s)) return;
    if (this.subMode !== "draw") return;
    // Draw sub-mode release — the poly-draft no-op return and the
    // drag-out shape commit (rect / ellipse / slot / line / freehand,
    // incl. the line-kind continuation) live in the composed
    // DrawSubModeController (#850).
    this.drawController.onUp(s, p);
  }

  /** Double-click finishes a polyline / polygon draft. */
  handleDoubleClick(p?: { x: number; y: number }) {
    // Edit + smooth-toggle sub-sub-mode only: double-click on an
    // anchor cycles its lock state (independent → angle → smooth).
    // Drag mode is intentionally excluded — drag mode is purely
    // for dragging operations; to change the lock state the user
    // must switch to the smooth-toggle sub-sub-tool. Keeps each
    // mode's responsibilities cleanly separated.
    if (this.subMode === "edit" && this.editAction === "smooth-toggle" && p) {
      const hit = this.findVisibleHandleHit(p);
      if (hit && hit.kind === "anchor") {
        this.toggleSmoothLock(
          hit.constructionId,
          hit.pathIndex,
          hit.segmentIndex,
        );
        return;
      }
    }
    if (this.subMode === "draw") {
      this.drawController.finishOnDoubleClick();
    }
  }

  /** Cycle the anchor's bezier state on each invocation:
   *  no-curve → independent → angle → smooth → no-curve. Each
   *  transition also nudges the geometry to match the new state
   *  (creating default handles when leaving no-curve, mirroring on
   *  smooth, zeroing on the way back to no-curve). State is stored
   *  as a combination of `smoothAnchors` entry presence/mode AND
   *  the geometry itself: an anchor with non-zero handles but no
   *  entry is "independent"; with zero handles and no entry it's
   *  "no-curve" — the user-visible default for sharp corners. */
  toggleSmoothLock(
    constructionId: string,
    pathIndex: number,
    segmentIndex: number,
  ) {
    const project = this.ctx.history.state;
    const line = projectConstructionLines(project).find(
      (l) => l.id === constructionId,
    );
    if (!line) return;
    // Issue #180 bidirectional editing: a smooth-toggle tap on a
    // mirror-derived line routes through the source root. The
    // cycle mutates the source's geometry (handles get bootstrapped
    // / mirrored / cleared) AND its smoothAnchors; the mirror then
    // re-derives via `deriveConstructionMirrors` (which propagates
    // smoothAnchors 1:1 thanks to paper.js preserving segment order
    // on the mirror's simple shapes). Without this routing, the
    // cycle would commit a pathData/smoothAnchors change to the
    // mirror itself — and the very next commit's derive would
    // overwrite it from the unchanged source ("snap back").
    let targetLine = line;
    if (isMirrorDerived(line)) {
      const route = walkMirrorChainToSource(
        project,
        constructionId,
        this.ctx.scene.getPaperScope(),
      );
      if (!route) {
        // Mirror-of-offset: no clean inverse. Same refusal contract
        // as the other bidirectional gestures. A toast hint here
        // arrives when PR #317 (canvas-toast helper) lands — for
        // now the silent early-return matches the historical
        // behaviour at the other refusal sites on this branch.
        return;
      }
      const sourceLine = projectConstructionLines(project).find(
        (l) => l.id === route.sourceRootId,
      );
      if (!sourceLine) return;
      targetLine = sourceLine;
    }
    const result = cycleAnchorBezier(
      targetLine,
      pathIndex,
      segmentIndex,
      this.ctx.scene.getPaperScope(),
    );
    if (!result) return;
    const scope = this.ctx.scene.getPaperScope();
    const targetId = targetLine.id;
    this.ctx.commit(result.label, (proj) => {
      const linesAfter = (proj.constructionLines ?? []).map((l) =>
        l.id === targetId
          ? dropConstructionPrimitive(
              l,
              proj,
              {
                pathData: result.pathData,
                smoothAnchors: result.smoothAnchors,
                bezierMemory: result.bezierMemory,
              },
              { preserveOrigin: true, scope },
            )
          : l,
      );
      return {
        ...proj,
        constructionLines: linesAfter,
        constraints: pruneInvalidCenterConstraints(
          linesAfter,
          proj.constraints,
        ),
      };
    });
  }

  /** Point-creation sub-sub-tool (#778): insert a new anchor at the
   *  clicked spot on a construction curve WITHOUT changing the
   *  visible shape. paper's `divideAt` performs the De Casteljau
   *  split, so the curve through the new anchor is geometrically
   *  identical to the original — the user gains an editable point,
   *  nothing moves.
   *
   *  The line drops its parametric primitive (a rect / ellipse with
   *  an extra anchor can't be regenerated from its parameters — the
   *  panel's dimension inputs would erase the new point), matching
   *  the slice / vertex-drag convention. Unlike slice, the per-anchor
   *  metadata CAN be carried over: `smoothAnchors` / `bezierMemory`
   *  entries after the insertion point shift by one index. The two
   *  anchors bounding a *curved* divided curve get their handle
   *  magnitudes rewritten by the split, so a "smooth" lock (equal
   *  magnitudes) no longer holds — demote it to "angle" (collinearity
   *  survives De Casteljau) and drop their stale handle snapshots. */
  addAnchorPoint(target: {
    constructionId: string;
    pathIndex: number;
    curveIndex: number;
    t: number;
  }): void {
    const project = this.ctx.history.state;
    const line = projectConstructionLines(project).find(
      (l) => l.id === target.constructionId,
    );
    if (!line) return;
    const scope = this.ctx.scene.getPaperScope();
    // The divideAt core (incl. the near-anchor EPS guard) is shared
    // with the piece outline editor (#969) via outlineEditGeometry;
    // the construction-only smoothAnchors / bezierMemory remap below
    // layers on top of the returned insertion index.
    const inserted = insertAnchorOnPathData(scope, line.pathData, target);
    if (!inserted) return;
    const newPathData = inserted.pathData;
    const dividedCurveHadHandles = inserted.dividedCurveHadHandles;
    const insertedAt = inserted.insertedAt;

    const targetId = target.constructionId;
    this.ctx.commit("Add point to construction line", (proj) => {
      const linesAfter = (proj.constructionLines ?? []).map((l) => {
        if (l.id !== targetId) return l;
        // No `scope` → `dropConstructionPrimitive` skips the final
        // `canonicalizeConstructionLine` pass. Canonicalize would
        // re-promote a still-straight 2-anchor result — and, more to
        // the point here, must never get a chance to collapse the
        // freshly-inserted collinear anchor on a straight line.
        return dropConstructionPrimitive(
          l,
          proj,
          {
            pathData: newPathData,
            // Index shift + smooth→angle demotion on the divided
            // curve's ends — shared with the leather Edit tool.
            ...remapAnchorMetaAfterInsert(l.smoothAnchors, l.bezierMemory, {
              pathIndex: target.pathIndex,
              insertedAt,
              dividedCurveHadHandles,
            }),
          },
          { preserveOrigin: false },
        );
      });
      return {
        ...proj,
        constructionLines: linesAfter,
        constraints: pruneInvalidCenterConstraints(
          linesAfter,
          proj.constraints,
        ),
      };
    });
    // Keep the edited line selected so its (now longer) segment list
    // stays in the panel and the new anchor's dot renders immediately.
    this.ctx.setSelection({
      pieceId: null,
      decorationId: null,
      constructionId: targetId,
    });
  }

  /** Discard the current draft (any kind). Returns true if there was
   *  one to drop. Delegated to the composed DrawSubModeController (#850). */
  setPolygonLiveCallbacks(
    live: (sides: number, radiusMm: number) => void,
    liveSideLength: (mm: number) => void,
    liveRadius: (mm: number) => void,
    clear: () => void,
  ): void {
    this.drawController.setPolygonLiveCallbacks(
      live,
      liveSideLength,
      liveRadius,
      clear,
    );
  }

  /** Re-render an in-flight regular-polygon stage after a panel value edit
   *  (#934) — app.ts calls this on settings changes. */
  refreshPolygonGesture(): void {
    this.drawController.refreshPolygonGesture();
  }

  handleDiscard(): boolean {
    return this.drawController.handleDiscard();
  }

  /** Escape pressed mid-drag of a polyline bezier-handle gesture
   *  (pen-tool style). Delegated to the draw engine, which pops the
   *  in-progress vertex + handle and leaves the rest of the draft intact.
   *  Returns true if there was a drag to abort, so the caller can
   *  preventDefault on the keypress without falling through to the
   *  commit-on-Escape branch. */
  handleEscapeDuringDrag(): boolean {
    return this.drawController.handleEscapeDuringDrag();
  }

  /** End a polyline / polygon at the last clicked vertex. Delegated to
   *  the composed DrawSubModeController (#850). */
  handleFinish(): boolean {
    return this.drawController.handleFinish();
  }

  /** Number of vertices in an in-progress polyline / polygon draft.
   *  Always 0 for drag-style kinds. */
  get vertexCount(): number {
    return this.drawController.vertexCount;
  }

  setDraftChangeListener(fn: ((vertexCount: number) => void) | null) {
    this.drawController.setDraftChangeListener(fn);
  }

  /** Full draft reset: the engine's poly draft (points / handles /
   *  continuation / preview) plus the drag-out draft state — all owned by
   *  the composed DrawSubModeController (#850), whose `cancelDraft` also
   *  resets the sibling offset / round / mirror controllers through the
   *  host (see `resetSiblingGestures`). Kept on the tool because many
   *  sub-mode transitions call it, and it is part of the `DrawGestureHost`
   *  contract the engine drives (`handleFinish` abandons a sub-2-vertex
   *  draft through it). */
  cancelDraft() {
    this.drawController.cancelDraft();
  }

  /** DrawSubModeHost: reset the sibling offset / round / mirror
   *  controllers, invoked from the DrawSubModeController's `cancelDraft`
   *  at the original inline position so a draw teardown still clears any
   *  adjacent gesture's transient state / overlays. */
  resetSiblingGestures(): void {
    this.offsetController.cancel();
    this.roundController.cancel();
    this.mirrorController.clear();
  }

  /** True when the line `id` is a derived offset OR mirror — its
   *  geometry is owned by a source line and direct edits (vertex /
   *  handle / curve / segment-delete) are refused. Whole-line drag
   *  on a mirror-derived line is the exception: it's routed
   *  bidirectionally through the source root via
   *  `tryRouteMirrorLineDragToSource` (issue #180 follow-up). */
  /** DeleteSliceHost hook. */
  isDerivedLineId(id: string): boolean {
    const line = projectConstructionLines(this.ctx.history.state).find(
      (l) => l.id === id,
    );
    return !!line && (isOffsetDerived(line) || isMirrorDerived(line));
  }

  /** Narrower predicate — true only when the line is an offset-
   *  derived line. The Delete sub-sub-tool uses this to allow
   *  whole-line removal of offsets (issue #327) while keeping
   *  mirror-derived lines refused (their delete story is tracked
   *  separately). */
  isOffsetDerivedLineId(id: string): boolean {
    const line = projectConstructionLines(this.ctx.history.state).find(
      (l) => l.id === id,
    );
    return !!line && isOffsetDerived(line);
  }

  /** True for a mirror-derived line (and NOT an offset). The Shift
   *  whole-line delete refuses these — mirrors can't be deleted
   *  directly, same as the per-target Delete (issue #670 follow-up). */
  isMirrorOnlyDerivedLineId(id: string): boolean {
    return this.isDerivedLineId(id) && !this.isOffsetDerivedLineId(id);
  }

  /** The construction line the Delete cursor is over, preferring an
   *  anchor / bezier handle hit, then the curve stroke. Drives the
   *  Shift whole-line highlight + delete (issue #670 follow-up). */
  /** DeleteSliceHost hook — line under the Delete cursor. */
  lineUnderCursorForDelete(
    p: { x: number; y: number },
    pt: paper.Point,
    handle: { constructionId: string; pathIndex: number } | null,
  ): { constructionId: string; pathIndex: number } | null {
    if (handle) {
      return {
        constructionId: handle.constructionId,
        pathIndex: handle.pathIndex,
      };
    }
    const curveHit = this.hitConstructionCurve(pt);
    if (curveHit) {
      return {
        constructionId: curveHit.constructionId,
        pathIndex: curveHit.pathIndex,
      };
    }
    return null;
  }

  /** Issue #180 bidirectional editing: when a whole-line drag
   *  starts on a mirror-derived line, walk the mirror chain to the
   *  source root and collect each axis matrix along the way. The
   *  drag's `constructionId` becomes the source root; per-frame
   *  cursor deltas are reflected through each axis's matrix in
   *  outermost-first order, then applied to the source. The mirror
   *  re-derives on every frame via the existing
   *  `rederiveConnectionsAffectedBy` call and ends up tracking the
   *  cursor.
   *
   *  Returns `null` when the chain hits an offset layer (V1 refuses
   *  bidirectional edit through offset math — there's no clean
   *  inverse for perpendicular-distance offsets) OR when an axis
   *  line / source has vanished mid-flight. Callers refuse the drag
   *  in that case so the geometry stays put. */
  tryRouteMirrorLineDragToSource(
    hitLineId: string,
  ): { sourceRootId: string; matrices: MirrorMatrix[] } | null {
    const proj = this.ctx.history.state;
    const lines = projectConstructionLines(proj);
    const byId = new Map(lines.map((l) => [l.id, l] as const));
    const start = byId.get(hitLineId);
    if (!start?.mirrorSource) return null;
    const scope = this.ctx.scene.getPaperScope();
    const matrices: MirrorMatrix[] = [];
    let cur: string = hitLineId;
    for (let i = 0; i < 1000; i++) {
      const line = byId.get(cur);
      if (!line) return null;
      if (!line.mirrorSource) {
        // Reached a non-mirror line. Refuse if it's offset-derived —
        // bidirectional editing through perpendicular-distance offset
        // math has no clean inverse in V1.
        if (isOffsetDerived(line)) return null;
        return { sourceRootId: cur, matrices };
      }
      // A SELF-axis mirror (#343) is the exception: when the axis belongs to
      // the source's own chain it TRAVELS with the source, so reflecting
      // across it after a translation gives `mirror + δ`, not `mirror − δ` —
      // the copy moves the SAME way the source does. Inverting the cursor
      // delta here would send the grabbed mirror off in the opposite
      // direction, which is what it used to do. Contribute no matrix and the
      // delta passes through unchanged.
      const axisRoot = resolveOffsetRoot(
        proj,
        line.mirrorSource.axis.constructionId,
      );
      const sourceRoot = resolveOffsetRoot(proj, line.mirrorSource.sourceId);
      if (axisRoot !== sourceRoot) {
        const axisSpec = resolveAxisSpec(proj, line.mirrorSource.axis, scope);
        if (!axisSpec) return null;
        matrices.push(buildMirrorMatrix(axisSpec));
      }
      cur = line.mirrorSource.sourceId;
    }
    return null;
  }

  // Public (not private) so the ResizeController's `ResizeHost` can clear
  // the shared canvas-preview slot during a resize drag (#838). The slot
  // (`previewItem` + the W×H `dimensionLabel`) physically lives on the
  // DrawSubModeController (#850); this delegates so the ResizeHost /
  // internal callers are unchanged.
  cleanupPreview() {
    this.drawController.cleanupPreview();
  }

  // ---- ResizeHost implementation (#838) -----------------------------
  // ConstructionTool owns a `ResizeController` (`this.resizeController`)
  // that runs the corner-resize gesture. Most collaborators
  // (getScope / getScene / getProject / getSettings / commit / rerender /
  // rotatableSelectedLineId) are shared with the RotationHost impl above;
  // these three inject the rest.

  /** ResizeHost: corner resize handles are a select-sub-mode-only
   *  affordance (they replace the corner-rotation ring there, #388). */
  isSelectSubMode(): boolean {
    return this.subMode === "select";
  }
  /** ResizeHost: store an item into the shared canvas-preview slot
   *  so a later `cleanupPreview` tears it down. The slot lives on the
   *  DrawSubModeController (#850). */
  setPreviewItem(item: paper.Item | null): void {
    this.drawController.setPreviewItem(item);
  }
  /** ResizeHost: push the in-progress resized line to the properties
   *  panel (null on drag end), mirroring the draw-time live readouts. */
  emitLiveResize(line: ConstructionLine | null): void {
    this.liveResizeCallback?.(line);
  }

  // ---- shared host accessors ---------------------------------------------
  // `getScope` / `getScene` serve every composed controller's host
  // interface (Rotation / Resize / Snap / Draw sub-mode, …), so they stay
  // on the tool as the single shared definition.

  getScope(): paper.PaperScope {
    return this.ctx.scene.getPaperScope();
  }
  getScene(): Scene {
    return this.ctx.scene;
  }

  /** DrawSubModeHost: the viewport's paper view (for the draw preview's
   *  W×H dimension label). */
  getViewportView(): paper.View {
    return this.ctx.viewport.view;
  }

  /** DrawSubModeHost: the shared construction snap tiers (#839) the draw
   *  draft snaps through. */
  getSnapController(): ConstructionSnapController {
    return this.snapController;
  }

  /** DrawSubModeHost: re-fire the tool's full `onMove` handler with
   *  synthesized modifiers (Draw-mode preview replay, #850). */
  dispatchOnMove(s: PointerSample, p: { x: number; y: number }): void {
    this.onMove(s, p);
  }

  /** DrawSubModeHost: re-fire the tool's full `onHover` handler. */
  dispatchOnHover(s: PointerSample, p: { x: number; y: number }): void {
    this.onHover(s, p);
  }
}
